import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import {
  Activity,
  AlertTriangle,
  AtSign,
  Bot,
  Briefcase,
  Building2,
  Calendar,
  CalendarClock,
  Check,
  ClipboardList,
  DollarSign,
  Flame,
  Globe,
  Hash,
  Linkedin,
  Loader2,
  Mail,
  MapPin,
  Phone,
  Plus,
  Receipt,
  ScanFace,
  Search,
  Send,
  Sparkles,
  Star,
  StickyNote,
  Target,
  Tag as TagIcon,
  Trash2,
  UserCircle2,
  Users,
  Video,
  Wand2,
  X,
  type LucideIcon,
} from 'lucide-react';
import { GlassPanel } from '../components/GlassPanel';
import { ArgoSuggestsStrip } from '../components/ArgoSuggestsStrip';
import { useProactiveNudges } from '../lib/agent/useProactiveNudges';
import { LoadingState } from '../components/States';
import { DocumentsPanel } from '../components/DocumentsPanel';
import { TasksPanel } from '../components/TasksPanel';
import { C } from '../lib/tokens';
import {
  bootstrapPeopleIfEmpty,
  createPerson,
  deletePerson,
  logPersonActivity,
  subscribePeople,
  updatePerson,
} from '../lib/db/people';
import {
  bootstrapAccountsIfEmpty,
  subscribeAccounts,
} from '../lib/db/accounts';
import {
  bootstrapAppUsersIfEmpty,
  subscribeAppUsers,
} from '../lib/db/appUsers';
import { enrichInfo, enrichProspect } from '../lib/agent/enrich';
import { captureEnrichmentToBrain } from '../lib/brain/companyBrain';
import { formatMeetNote, meetInfo, pullMeetingNotes } from '../lib/agent/meetNotes';
import { emailInfo, formatEmailActivity, pullEmailThreads } from '../lib/agent/emailLog';
import { uuid } from '../lib/db/store';
import type {
  Account,
  AppUser,
  BillingDate,
  ContactPOC,
  PersonDeliverable,
  Person,
  PersonActivity,
  PersonCategory,
  PersonaProfile,
} from '../lib/db/types';
import {
  isFollowUpOverdue,
  scoreLead,
  type LeadBand,
} from '../lib/crm/score';
import {
  scoreCustomerHealth,
  type HealthBand,
} from '../lib/crm/health';
import {
  computeWinLoss,
  reasonsForStage,
  type ReasonTally,
  type SegmentStat,
} from '../lib/crm/winloss';
import {
  briefToMarkdown,
  composePreMeetingBrief,
  type PreMeetingBrief,
} from '../lib/crm/preMeetingBrief';
import {
  COMPANY_SIZE_OPTIONS,
  PERSONA_FEEDS,
  PERSONA_PURPOSE,
  PERSONA_TEMPLATES,
  personaCompleteness,
  personaTemplate,
  SENIORITY_OPTIONS,
  synthesizePersona,
  validatePersonaDraft,
  type PersonaDraft,
  type PersonaTemplate,
} from '../lib/crm/personas';
import { AccountsCrmView } from './AccountsCrmView';
import { useAuth } from '../lib/auth/AuthProvider';
import { useFocusTrap } from '../lib/useFocusTrap';

/**
 * People — a simple CRM that surfaces contacts by category.
 *
 * Storage today is the local async store (localStorage-backed). The
 * shape of every Person record matches what we plan to push to
 * Firestore, so the view changes nothing when we cut over — only the
 * adapter behind `subscribePeople` / `createPerson` etc. swaps.
 *
 * The Personas tab is a full structured-ICP creator: spin a persona up
 * from a starter template or synthesize one from the real CRM, then refine
 * every dimension (goals, pain points, objections, decision criteria…)
 * inline. Stored on the `people` collection as category `persona`.
 */

/**
 * The Contacts view (`PeopleView` is the legacy filename) is the
 * place to look at our network at every grain:
 *
 *   - Accounts   : company-level CRM (one row per account)
 *   - Contacts   : individual people at customer accounts
 *   - Prospects  : individual people we are pursuing pre-conversion
 *   - Personas   : structured ICP archetypes (deep-linked from Tools)
 *
 * The Staff tab was retired — Argo team members are managed under
 * Admin → Users now, and per-account ownership pulls from there.
 */
type TabKey = 'accounts' | 'contacts' | 'prospects' | 'persona';

/** Deep-link contract — the Contacts top-menu dropdown sends one of
 *  these directly. Exported so TopMenu can type its handler. */
export type ContactsTab = TabKey;

/**
 * Person-list ordering. `fit` surfaces the enrichment ICP fit score — which
 * was previously computed by the enrichment seam but only ever visible deep
 * in a prospect's detail view — as a first-class way to triage the list.
 * `lead` uses the on-demand engagement/value lead score; `value` the deal
 * size; `name` is the alphabetical default.
 */
type SortKey = 'name' | 'fit' | 'lead' | 'value';

/** Fit-score buckets for the list filter. Cutoffs mirror the lead-score
 *  bands (70 / 40) so "strong / medium / low" read consistently across the
 *  CRM. `unscored` isolates prospects that haven't been enriched yet. */
type FitFilter = 'all' | 'strong' | 'medium' | 'low' | 'unscored';

// Remember the prospect-list triage controls across reloads. Sorting by fit
// and filtering to "strong" is a deliberate working set a rep returns to all
// day; resetting it to Name/All on every navigation made the controls feel
// disposable. Mirrors the AccountsCrmView `argo.accounts.view` persistence.
const PEOPLE_SORT_KEY = 'argo.people.sort';
const PEOPLE_FIT_FILTER_KEY = 'argo.people.fitFilter';

const SORT_KEYS: readonly SortKey[] = ['name', 'fit', 'lead', 'value'];
const FIT_FILTERS: readonly FitFilter[] = ['all', 'strong', 'medium', 'low', 'unscored'];

/** Read a persisted control value, validating against the known set so a
 *  stale or hand-edited localStorage entry can never wedge the list into an
 *  unrenderable state — anything unrecognized falls back to the default. */
function readPersisted<T extends string>(
  key: string,
  allowed: readonly T[],
  fallback: T,
): T {
  try {
    const raw = localStorage.getItem(key);
    return raw && (allowed as readonly string[]).includes(raw) ? (raw as T) : fallback;
  } catch {
    return fallback;
  }
}

/** The enrichment fit score (0–100) if the person has been enriched. */
function fitOf(p: Person): number | undefined {
  return p.enrichment?.fitScore;
}

/** Deal (prospect) or account (customer) dollar value; 0 when unset, so
 *  value-sorting drops unvalued records to the bottom. */
function valueOf(p: Person): number {
  return p.estimatedValue ?? p.accountValue ?? 0;
}

/**
 * Entity quick-jump target handed down from App (⌘K palette → onJump).
 * A person jump lands on its category tab with the row selected; an
 * account jump switches to the Accounts tab and opens its detail modal.
 * The nonce lets the same record be re-focused twice in a row.
 */
export interface PeopleFocus {
  kind: 'person' | 'account';
  id: string;
  /** For person jumps — the contacts sub-tab that lists this category. */
  tab?: ContactsTab;
  nonce: number;
}

interface PeopleViewProps {
  /** Initial tab. Driven by the Contacts top-menu dropdown. */
  initialTab?: TabKey;
  /** A record to land on directly (from the ⌘K command palette). */
  focus?: PeopleFocus | null;
}

/** Tabs visible in the picker. Persona is intentionally absent — it's
 *  reachable via Tools → Personas only, since it's a tool-shaped
 *  surface rather than a contact category. */
const visibleTabsInPicker: TabKey[] = ['accounts', 'contacts', 'prospects'];

interface TabMeta {
  id: TabKey;
  label: string;
  plural: string;
  icon: LucideIcon;
  /** Which PersonCategory this tab filters when it lists People. The
   *  Accounts tab doesn't read from the people collection at all. */
  personCategory?: PersonCategory;
}

const tabs: TabMeta[] = [
  { id: 'accounts', label: 'Accounts', plural: 'accounts', icon: Building2 },
  {
    id: 'contacts',
    label: 'Contacts',
    plural: 'contacts',
    icon: UserCircle2,
    personCategory: 'customer',
  },
  {
    id: 'prospects',
    label: 'Prospects',
    plural: 'prospects',
    icon: Target,
    personCategory: 'prospect',
  },
  {
    id: 'persona',
    label: 'Personas',
    plural: 'personas',
    icon: ScanFace,
    personCategory: 'persona',
  },
];

const tabMeta = (id: TabKey) => tabs.find((t) => t.id === id)!;

/**
 * Curated dropdown options. Free-text typing for these fields drifted
 * into inconsistent spellings ("LinkedIn" vs "linkedin" vs "LI"), which
 * broke source-attribution reporting — a fixed list keeps the funnel
 * analytics clean. Owners are derived from the AppUsers collection at
 * runtime; these are the lead-source channels.
 */
const SOURCE_OPTIONS = [
  '',
  'Inbound',
  'Outbound',
  'Referral',
  'LinkedIn',
  'Cold email',
  'Event',
  'Webinar',
  'Content',
  'Partner',
  'Existing customer',
  'Other',
] as const;

export function PeopleView({ initialTab = 'accounts', focus = null }: PeopleViewProps) {
  const { user } = useAuth();
  const canMutate = user?.role !== 'viewer';
  // Proactive nudges — the agent's suggested next moves for this book,
  // computed live by the tested engine (see lib/agent/proactiveNudges).
  const nudges = useProactiveNudges();
  const [tab, setTab] = useState<TabKey>(initialTab);
  const [people, setPeople] = useState<Person[]>([]);
  // First-snapshot flag so we show a skeleton, not a premature "none match".
  const [loaded, setLoaded] = useState(false);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [users, setUsers] = useState<AppUser[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [editing, setEditing] = useState<boolean>(false);
  const [draft, setDraft] = useState<Person | null>(null);
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>(() =>
    readPersisted(PEOPLE_SORT_KEY, SORT_KEYS, 'name'),
  );
  const [fitFilter, setFitFilter] = useState<FitFilter>(() =>
    readPersisted(PEOPLE_FIT_FILTER_KEY, FIT_FILTERS, 'all'),
  );
  const lastInitialTab = useRef<TabKey>(initialTab);
  // When a focus jump switches tabs, the [tab] effect clears the selection;
  // this lets the jump's selection survive that clear (applied once the new
  // tab settles). The nonce ref dedupes repeated focus deliveries.
  const pendingSelect = useRef<string | null>(null);
  const lastFocusNonce = useRef(0);

  // Persist the triage controls so a rep's working set (e.g. sort by fit,
  // filter to strong) survives reloads and tab switches. Storage failures
  // are non-fatal — the controls still work, they just won't be remembered.
  useEffect(() => {
    try {
      localStorage.setItem(PEOPLE_SORT_KEY, sortKey);
    } catch {
      /* storage unavailable — non-fatal */
    }
  }, [sortKey]);
  useEffect(() => {
    try {
      localStorage.setItem(PEOPLE_FIT_FILTER_KEY, fitFilter);
    } catch {
      /* storage unavailable — non-fatal */
    }
  }, [fitFilter]);

  // Subscribe to the people collection. The initial snapshot fires
  // immediately, so this also bootstraps the list on mount.
  useEffect(() => {
    let active = true;
    bootstrapPeopleIfEmpty().catch(() => {
      // Seed failures aren't fatal — UI still works with an empty list.
    });
    bootstrapAccountsIfEmpty().catch(() => undefined);
    bootstrapAppUsersIfEmpty().catch(() => undefined);
    const unsubPeople = subscribePeople((records) => {
      if (active) {
        setPeople(records);
        setLoaded(true);
      }
    });
    const unsubAccounts = subscribeAccounts((records) => {
      if (active) setAccounts(records);
    });
    const unsubUsers = subscribeAppUsers((records) => {
      if (active) setUsers(records);
    });
    return () => {
      active = false;
      unsubPeople();
      unsubAccounts();
      unsubUsers();
    };
  }, []);

  // If the dropdown re-fires with a new tab while we are already
  // mounted, switch tabs and clear the right-hand pane.
  useEffect(() => {
    if (initialTab !== lastInitialTab.current) {
      setTab(initialTab);
      setSelectedId(null);
      setEditing(false);
      setDraft(null);
      lastInitialTab.current = initialTab;
    }
  }, [initialTab]);

  // Switching tabs clears the selection so the detail pane reflects the
  // visible list — unless a focus jump asked to land on a specific row in
  // the new tab, in which case we apply that selection instead of clearing.
  useEffect(() => {
    setEditing(false);
    setDraft(null);
    if (pendingSelect.current) {
      setSelectedId(pendingSelect.current);
      pendingSelect.current = null;
    } else {
      setSelectedId(null);
    }
  }, [tab]);

  // Entity quick-jump: land on the record chosen in the ⌘K palette.
  // Accounts switch to the Accounts tab (AccountsCrmView opens the modal
  // from its own focus prop); people select their row on the right tab.
  useEffect(() => {
    if (!focus || focus.nonce === lastFocusNonce.current) return;
    lastFocusNonce.current = focus.nonce;
    if (focus.kind === 'account') {
      setTab('accounts');
      return;
    }
    const targetTab = focus.tab ?? 'contacts';
    if (targetTab === tab) {
      // Same tab — select directly (the [tab] effect won't fire).
      setSelectedId(focus.id);
    } else {
      // Different tab — stash the selection so it survives the tab switch.
      pendingSelect.current = focus.id;
      setTab(targetTab);
    }
  }, [focus, tab]);

  const counts = useMemo(() => {
    const acc: Record<TabKey, number> = {
      accounts: accounts.length,
      contacts: 0,
      prospects: 0,
      persona: 0,
    };
    for (const p of people) {
      if (p.category === 'customer') acc.contacts++;
      else if (p.category === 'prospect') acc.prospects++;
      else if (p.category === 'persona') acc.persona++;
    }
    return acc;
  }, [people, accounts]);

  const meta = tabMeta(tab);
  const personCategory = meta.personCategory;
  // Capitalized alias so the tab's icon can render as a component in the
  // empty-state teaching card below (JSX needs an uppercase identifier).
  const MetaIcon = meta.icon;

  const filtered = useMemo(() => {
    if (!personCategory) return [] as Person[];
    const q = search.trim().toLowerCase();
    const rows = people
      .filter((p) => p.category === personCategory)
      .filter((p) => {
        if (!q) return true;
        return (
          p.name.toLowerCase().includes(q) ||
          (p.email ?? '').toLowerCase().includes(q) ||
          (p.company ?? '').toLowerCase().includes(q) ||
          (p.title ?? '').toLowerCase().includes(q) ||
          p.tags.some((t) => t.toLowerCase().includes(q))
        );
      })
      .filter((p) => {
        // Fit filter operates on the enrichment ICP score. `unscored`
        // isolates the not-yet-enriched; the banded options require a score.
        if (fitFilter === 'all') return true;
        const f = fitOf(p);
        if (fitFilter === 'unscored') return f === undefined;
        if (f === undefined) return false;
        if (fitFilter === 'strong') return f >= 70;
        if (fitFilter === 'medium') return f >= 40 && f < 70;
        return f < 40; // 'low'
      });

    // Sort on a copy; every comparator falls back to name so ties are stable
    // and legible. Missing fit scores sort last (sentinel -1 < any 0–100).
    const byName = (a: Person, b: Person) => a.name.localeCompare(b.name);
    const sorted = [...rows];
    switch (sortKey) {
      case 'fit':
        sorted.sort((a, b) => (fitOf(b) ?? -1) - (fitOf(a) ?? -1) || byName(a, b));
        break;
      case 'lead':
        sorted.sort((a, b) => scoreLead(b).score - scoreLead(a).score || byName(a, b));
        break;
      case 'value':
        sorted.sort((a, b) => valueOf(b) - valueOf(a) || byName(a, b));
        break;
      default:
        sorted.sort(byName);
    }
    return sorted;
  }, [people, personCategory, search, sortKey, fitFilter]);

  const selected = selectedId ? people.find((p) => p.id === selectedId) ?? null : null;

  const beginAdd = async () => {
    if (!personCategory) return;
    const created = await createPerson({
      category: personCategory,
      name: 'New contact',
      tags: [],
    });
    setSelectedId(created.id);
    setDraft(created);
    setEditing(true);
  };

  const beginEdit = () => {
    if (!selected) return;
    setDraft({ ...selected });
    setEditing(true);
  };

  const cancelEdit = () => {
    setDraft(null);
    setEditing(false);
  };

  const saveEdit = async () => {
    if (!draft) return;
    const patch: Person = { ...draft };
    // Win-loss bookkeeping: stamp the close date when a prospect first
    // reaches a closing stage, and clear the win-loss capture if it moves
    // back out of one (so a reopened deal doesn't carry a stale reason).
    const wasClosing =
      selected?.stage === 'closed-won' || selected?.stage === 'closed-lost';
    const isClosing =
      patch.stage === 'closed-won' || patch.stage === 'closed-lost';
    if (isClosing && !patch.closedAt) patch.closedAt = new Date().toISOString();
    if (!isClosing) {
      patch.closedAt = undefined;
      patch.winLossReason = undefined;
      patch.winLossNote = undefined;
    }
    await updatePerson(draft.id, patch);
    // Log the close so it shows in the contact timeline (and the activity
    // feed) the first time it lands — not on every later edit.
    if (isClosing && !wasClosing) {
      const won = patch.stage === 'closed-won';
      const reason = patch.winLossReason ? ` — ${patch.winLossReason}` : '';
      await logPersonActivity(
        draft.id,
        'stage',
        `Deal ${won ? 'won' : 'lost'}${reason}`,
      );
    }
    setEditing(false);
    setDraft(null);
  };

  const removeSelected = async () => {
    if (!selected) return;
    await deletePerson(selected.id);
    setSelectedId(null);
    setEditing(false);
    setDraft(null);
  };

  // Close the detail modal — clears the selection and any in-flight edit
  // draft so reopening starts clean.
  const closeDetail = () => {
    setSelectedId(null);
    setEditing(false);
    setDraft(null);
  };

  const logActivity = async (kind: PersonActivity['kind'], text: string) => {
    if (!selected) return;
    await logPersonActivity(selected.id, kind, text);
    // The people subscription refires, so `selected` re-derives with the
    // new entry — no local state to keep in sync.
  };

  // Owner dropdown options: active AppUsers by display name, plus any
  // owner already on a record (so we never drop a legacy free-text owner).
  const ownerOptions = useMemo(() => {
    const set = new Set<string>();
    for (const u of users) {
      if (u.status !== 'disabled') set.add(u.displayName || u.email);
    }
    for (const p of people) {
      if (p.ownerName) set.add(p.ownerName);
    }
    return ['', ...Array.from(set).sort((a, b) => a.localeCompare(b))];
  }, [users, people]);

  // Run the enrichment seam against a prospect and persist the result.
  // Marks `pending` first so the UI can show a spinner, then writes the
  // evaluation (summary + signals + fit score) and an activity entry.
  const enrich = async (person: Person) => {
    await updatePerson(person.id, {
      enrichment: { status: 'pending', evaluatedAt: new Date().toISOString() },
    });
    const result = await enrichProspect({
      name: person.name,
      company: person.company,
      title: person.title,
      linkedinUrl: person.linkedinUrl,
      website: person.website,
    });
    await updatePerson(person.id, {
      enrichment: result,
      ...(result.fitScore !== undefined ? { score: result.fitScore } : {}),
    });
    const actor = enrichInfo().configured ? 'Enrichment worker' : 'Argo (simulated)';
    await logPersonActivity(
      person.id,
      'system',
      `Enrichment ${result.status === 'done' ? 'completed' : result.status} — ${
        result.summary ?? 'no summary'
      }`,
      actor,
    );
    // Deploy capture → Brain: pipe the read into the matching account's
    // Company Brain as research-sourced facts. No-op when the prospect's
    // company doesn't map to a known account.
    //
    // Only capture when a REAL enrichment worker produced the result — we
    // never persist simulated/fabricated signals into the Company Brain
    // (a knowledge base mixing fake "research" facts in is the worst trap).
    // The simulated read still shows on the prospect + activity log (both
    // marked "(simulated)"), it just doesn't pollute the Brain. See AUDIT.md §4B.
    if (result.status === 'done' && enrichInfo().configured) {
      await captureEnrichmentToBrain({
        company: person.company,
        personName: person.name,
        summary: result.summary,
        signals: result.signals,
        fitScore: result.fitScore,
        setBy: actor,
      }).catch(() => undefined);
    }
  };

  // Pull Google Meet notes for a contact via the GSuite seam (live
  // worker when configured, deterministic simulation otherwise) and
  // append each as a `meeting` activity entry. Returns the count so the
  // button can surface "Synced N notes".
  const syncMeetNotes = async (person: Person): Promise<number> => {
    const notes = await pullMeetingNotes({
      name: person.name,
      company: person.company,
      email: person.email,
    });
    const actor = meetInfo().configured ? 'Google Meet' : 'Google Meet (simulated)';
    // Oldest-first so the most-recent meeting ends up last-appended.
    for (const note of [...notes].sort((a, b) => a.at.localeCompare(b.at))) {
      await logPersonActivity(person.id, 'meeting', formatMeetNote(note), actor);
    }
    return notes.length;
  };

  // Pull recent Gmail threads for a contact via the same GSuite seam and
  // append each new one as an `email` activity entry. Idempotent: a
  // formatted line already on the timeline is skipped (the simulation is
  // deterministic per contact, so re-syncing would otherwise duplicate).
  // Returns the count of newly added messages for the button copy.
  const syncEmailLog = async (person: Person): Promise<number> => {
    const messages = await pullEmailThreads({
      name: person.name,
      company: person.company,
      email: person.email,
    });
    const actor = emailInfo().configured ? 'Gmail' : 'Gmail (simulated)';
    const seen = new Set(
      (person.activity ?? [])
        .filter((a) => a.kind === 'email')
        .map((a) => a.text),
    );
    const fresh = messages.filter((m) => !seen.has(formatEmailActivity(m)));
    // Oldest-first so the most-recent message ends up last-appended.
    for (const msg of [...fresh].sort((a, b) => a.at.localeCompare(b.at))) {
      await logPersonActivity(person.id, 'email', formatEmailActivity(msg), actor);
    }
    return fresh.length;
  };

  // The People view header (eyebrow / "Your network…" / description)
  // has been retired by request — the tab picker is the only chrome.
  // Accounts tab uses a wider container so the CRM table can breathe;
  // other tabs keep the standard 1280px app-container.
  const isAccountsTab = tab === 'accounts';
  const wrapperClass = isAccountsTab ? 'people-shell people-shell--wide' : 'app-container';

  return (
    <div className={wrapperClass} style={{ display: 'grid', gap: 16 }}>
      {/* Header is intentionally omitted on every tab. The "Add" CTA
          lives inline in the relevant child views (CRM toolbar for
          accounts; person tabs render this Add header below). */}
      {(tab === 'contacts' || tab === 'prospects') && canMutate && (
        <header
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 16,
          }}
        >
          <button className="glass-btn glass-btn--primary" onClick={beginAdd}>
            <Plus size={13} /> Add {meta.label.replace(/s$/, '').toLowerCase()}
          </button>
        </header>
      )}

      {/* Tabs */}
      <GlassPanel variant="subtle" animate={false} style={{ padding: '10px 14px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
          {visibleTabsInPicker.map((t) => {
            const m = tabMeta(t);
            const Icon = m.icon;
            const active = t === tab;
            return (
              <button
                key={t}
                type="button"
                onClick={() => setTab(t)}
                className="glass-btn"
                style={{
                  padding: '6px 12px',
                  border: `1px solid ${active ? 'rgba(255,0,255,0.45)' : 'rgba(255,255,255,0.10)'}`,
                  background: active
                    ? 'linear-gradient(135deg, rgba(255,0,255,0.20), rgba(187,65,152,0.12))'
                    : 'transparent',
                  color: active ? '#fff' : C.textMuted,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 8,
                }}
              >
                <Icon size={13} />
                <span>{m.label}</span>
                <span
                  className="t-meta"
                  style={{
                    padding: '0 6px',
                    borderRadius: 999,
                    background: active ? 'rgba(255,255,255,0.12)' : 'rgba(255,255,255,0.05)',
                    color: active ? '#fff' : C.textDim,
                  }}
                >
                  {counts[t]}
                </span>
              </button>
            );
          })}
        </div>
      </GlassPanel>

      {/* Proactive "Argo suggests" strip — the same tested nudge engine as
          the Copilot dock, surfaced where the CRM work happens. Accepting a
          suggestion queues the governed action into the Dashboard approvals
          inbox; renders nothing when the book is clean. */}
      <ArgoSuggestsStrip nudges={nudges} canMutate={canMutate} people={people} />

      {tab === 'prospects' && <WinLossPanel people={people} />}

      {tab === 'accounts' ? (
        // CRM-style account list. No extra header/intro text — the
        // tab switcher above is the only chrome.
        <AccountsCrmView
          focusAccount={
            focus && focus.kind === 'account'
              ? { id: focus.id, nonce: focus.nonce }
              : null
          }
        />
      ) : tab === 'persona' ? (
        <PersonasPanel
          personas={people.filter((p) => p.category === 'persona')}
          allPeople={people}
          canMutate={canMutate}
        />
      ) : (
        <>
          {/* Full-width list — each row opens a click-through detail
              modal, matching the Accounts CRM experience. */}
          <GlassPanel style={{ padding: '14px 16px' }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                flexWrap: 'wrap',
                padding: '0 4px 12px',
              }}
            >
              <div style={{ position: 'relative', flex: '1 1 240px', maxWidth: 380 }}>
                <Search
                  size={13}
                  style={{
                    position: 'absolute',
                    top: '50%',
                    left: 14,
                    transform: 'translateY(-50%)',
                    color: C.textDim,
                    marginTop: -6,
                  }}
                />
                <input
                  className="glass-input"
                  placeholder={`Search ${meta.plural}…`}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  style={{ paddingLeft: 32, fontSize: 12, width: '100%' }}
                />
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginLeft: 'auto', flexWrap: 'wrap' }}>
                <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  <Target size={13} style={{ color: C.textDim }} />
                  <select
                    className="glass-input"
                    value={fitFilter}
                    onChange={(e) => setFitFilter(e.target.value as FitFilter)}
                    title="Filter by enrichment ICP fit score"
                    style={{ fontSize: 12, padding: '6px 8px' }}
                  >
                    <option value="all">All fit</option>
                    <option value="strong">Strong fit (70+)</option>
                    <option value="medium">Medium fit (40–69)</option>
                    <option value="low">Low fit (&lt;40)</option>
                    <option value="unscored">Not enriched</option>
                  </select>
                </label>
                <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  <span className="t-meta">Sort</span>
                  <select
                    className="glass-input"
                    value={sortKey}
                    onChange={(e) => setSortKey(e.target.value as SortKey)}
                    title="Order the list"
                    style={{ fontSize: 12, padding: '6px 8px' }}
                  >
                    <option value="name">Name (A–Z)</option>
                    <option value="fit">Fit score</option>
                    <option value="lead">Lead score</option>
                    <option value="value">Deal value</option>
                  </select>
                </label>
              </div>
            </div>

            {!loaded ? (
              <LoadingState rows={4} label={`Loading ${meta.plural}`} />
            ) : filtered.length === 0 ? (
              // Two distinct empty states. A genuinely empty store (no records
              // of this category, no active search/fit filter) gets a teaching
              // card with a primary "Add your first X" CTA — the same pattern
              // PersonasPanel uses and UX_REVIEW move #1 calls for. When records
              // exist but the filters hid them, show a plain "no match" line
              // that names what was filtered, so the operator knows to relax it.
              !people.some((p) => p.category === personCategory) &&
              search.trim() === '' &&
              fitFilter === 'all' ? (
                <div
                  style={{
                    display: 'grid',
                    placeItems: 'center',
                    gap: 12,
                    textAlign: 'center',
                    maxWidth: 480,
                    margin: '0 auto',
                    padding: '32px 24px',
                  }}
                >
                  <span
                    style={{
                      width: 56,
                      height: 56,
                      borderRadius: 16,
                      display: 'inline-grid',
                      placeItems: 'center',
                      background:
                        'linear-gradient(135deg, rgba(255,0,255,0.20), rgba(187,65,152,0.12))',
                      border: '1px solid rgba(255,255,255,0.14)',
                      color: '#fff',
                    }}
                  >
                    <MetaIcon size={26} strokeWidth={1.75} />
                  </span>
                  <h3 className="t-section-title" style={{ margin: 0 }}>
                    No {meta.plural} yet
                  </h3>
                  <p className="t-body" style={{ color: C.textMuted, margin: 0 }}>
                    Your {meta.plural} will show up here. Add one by hand, or ask
                    Argo in chat to create {meta.plural} for you — every write
                    waits for your confirmation.
                  </p>
                  {canMutate && (
                    <button
                      className="glass-btn glass-btn--primary"
                      style={{ marginTop: 4 }}
                      onClick={beginAdd}
                    >
                      <Plus size={13} /> Add your first{' '}
                      {meta.label.replace(/s$/, '').toLowerCase()}
                    </button>
                  )}
                </div>
              ) : (
                <div
                  className="t-meta"
                  style={{ padding: '24px 12px', textAlign: 'center' }}
                >
                  No {meta.plural} match
                  {search.trim() ? ` “${search.trim()}”` : ' these filters'}.
                </div>
              )
            ) : (
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
                  gap: 8,
                }}
              >
                {filtered.map((p) => (
                  <button
                    key={p.id}
                    onClick={() => {
                      setSelectedId(p.id);
                      setEditing(false);
                      setDraft(null);
                    }}
                    className="glass-row"
                    style={{
                      borderRadius: 12,
                      border: '1px solid rgba(255,255,255,0.06)',
                      background: 'rgba(255,255,255,0.02)',
                      padding: '10px 12px',
                      cursor: 'pointer',
                      textAlign: 'left',
                      display: 'flex',
                      gap: 10,
                      alignItems: 'center',
                    }}
                  >
                    <Avatar name={p.name} category={p.category} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div
                        className="t-card-title"
                        style={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 6,
                        }}
                      >
                        <span
                          style={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {p.name}
                        </span>
                        {isFollowUpOverdue(p) && (
                          <CalendarClock
                            size={12}
                            style={{ color: C.orangeWarm, flexShrink: 0 }}
                          />
                        )}
                      </div>
                      <div
                        className="t-meta"
                        style={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {[p.title, p.company].filter(Boolean).join(' · ') || p.email || '—'}
                      </div>
                    </div>
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-end',
                        gap: 4,
                        flexShrink: 0,
                      }}
                    >
                      <CategorySignal person={p} />
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        {fitOf(p) !== undefined && <FitBadge score={fitOf(p)!} />}
                        <LeadScoreBadge person={p} size="sm" />
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </GlassPanel>

          {/* Click-through detail modal. */}
          {(selected || (editing && draft)) && (
            <PersonDetailModal onClose={closeDetail}>
              {selected && !editing && (
                <DetailReadView
                  person={selected}
                  allPeople={people}
                  canMutate={canMutate}
                  onEdit={beginEdit}
                  onDelete={removeSelected}
                  onLogActivity={logActivity}
                  onEnrich={enrich}
                  onSyncMeet={syncMeetNotes}
                  onSyncEmail={syncEmailLog}
                />
              )}
              {editing && draft && (
                <DetailEditView
                  draft={draft}
                  onChange={setDraft}
                  onCancel={cancelEdit}
                  onSave={saveEdit}
                  onDelete={removeSelected}
                  ownerOptions={ownerOptions}
                />
              )}
            </PersonDetailModal>
          )}
        </>
      )}
    </div>
  );
}

// --- Avatar -----------------------------------------------------------------

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).slice(0, 2);
  return parts.map((p) => p[0]?.toUpperCase() ?? '').join('') || '?';
}

const categoryGradient: Record<PersonCategory, string> = {
  customer: 'linear-gradient(135deg, #22C55E, #027A48)',
  prospect: 'linear-gradient(135deg, #FF00FF, #BB4198)',
  staff: 'linear-gradient(135deg, #0082F3, #2F01FD)',
  persona: 'linear-gradient(135deg, #FDAD01, #E23939)',
};

function Avatar({
  name,
  category,
  size = 32,
}: {
  name: string;
  category: PersonCategory;
  size?: number;
}) {
  return (
    <span
      style={{
        width: size,
        height: size,
        borderRadius: '50%',
        background: categoryGradient[category],
        border: '1px solid rgba(255,255,255,0.18)',
        color: '#fff',
        fontSize: size * 0.36,
        fontWeight: 700,
        display: 'inline-grid',
        placeItems: 'center',
        flexShrink: 0,
      }}
    >
      {initials(name)}
    </span>
  );
}

// --- Category signal pill in the row ---------------------------------------

function CategorySignal({ person }: { person: Person }) {
  if (person.category === 'customer' && person.status) {
    const tone =
      person.status === 'active'
        ? 'glass-pill--green'
        : person.status === 'paused'
          ? 'glass-pill--orange'
          : 'glass-pill--magenta';
    return <span className={`glass-pill ${tone}`}>{person.status}</span>;
  }
  if (person.category === 'prospect' && person.stage) {
    return <span className="glass-pill glass-pill--magenta">{person.stage}</span>;
  }
  if (person.category === 'staff' && person.team) {
    return <span className="glass-pill glass-pill--blue">{person.team}</span>;
  }
  return null;
}

// --- Lead score ------------------------------------------------------------

/** Visual treatment per lead band. */
const bandStyle: Record<LeadBand, { bg: string; border: string; text: string; label: string }> = {
  hot: {
    bg: 'linear-gradient(135deg, rgba(255,0,255,0.22), rgba(226,57,57,0.16))',
    border: 'rgba(255,0,255,0.45)',
    text: '#ffffff',
    label: 'Hot',
  },
  warm: {
    bg: 'rgba(253,173,1,0.14)',
    border: 'rgba(253,173,1,0.40)',
    text: '#ffcf7a',
    label: 'Warm',
  },
  cold: {
    bg: 'rgba(0,130,243,0.12)',
    border: 'rgba(0,130,243,0.32)',
    text: '#8fc0ff',
    label: 'Cold',
  },
};

/**
 * Compact enrichment ICP-fit pill for list rows. Distinct from the lead-score
 * badge (engagement/value) by its Target icon + green/amber/muted banding, so
 * the two scores never read as one. Cutoffs (70 / 40) match the lead bands.
 * Only rendered when a fit score exists, so it doubles as an "enriched" marker.
 */
function FitBadge({ score }: { score: number }) {
  const tone =
    score >= 70
      ? { bg: 'rgba(34,197,94,0.14)', border: 'rgba(34,197,94,0.40)', text: '#7ee2a8' }
      : score >= 40
        ? { bg: 'rgba(253,173,1,0.12)', border: 'rgba(253,173,1,0.34)', text: '#ffcf7a' }
        : { bg: 'rgba(255,255,255,0.05)', border: 'rgba(255,255,255,0.14)', text: C.textMuted };
  return (
    <span
      title={`ICP fit ${score}/100 — from prospect enrichment`}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 3,
        padding: '1px 7px',
        borderRadius: 999,
        fontSize: 11,
        fontWeight: 700,
        background: tone.bg,
        border: `1px solid ${tone.border}`,
        color: tone.text,
        lineHeight: 1.6,
      }}
    >
      <Target size={10} />
      {score}
    </span>
  );
}

function LeadScoreBadge({ person, size }: { person: Person; size: 'sm' | 'lg' }) {
  const { score, band, factors } = scoreLead(person);
  const s = bandStyle[band];
  const tip = factors
    .map((f) => `${f.label}: ${f.points > 0 ? '+' : ''}${f.points}`)
    .join('\n');

  if (size === 'sm') {
    return (
      <span
        title={`Lead score ${score} (${s.label})\n${tip}`}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 4,
          padding: '1px 7px',
          borderRadius: 999,
          fontSize: 11,
          fontWeight: 700,
          background: s.bg,
          border: `1px solid ${s.border}`,
          color: s.text,
          lineHeight: 1.6,
        }}
      >
        {band === 'hot' && <Flame size={10} />}
        {score}
      </span>
    );
  }

  return (
    <div
      title={tip}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '8px 14px',
        borderRadius: 14,
        background: s.bg,
        border: `1px solid ${s.border}`,
      }}
    >
      <div style={{ textAlign: 'right' }}>
        <div className="t-meta" style={{ lineHeight: 1.1 }}>Lead score</div>
        <div style={{ fontSize: 22, fontWeight: 800, lineHeight: 1.05, color: s.text }}>
          {score}
        </div>
      </div>
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 4,
          padding: '2px 8px',
          borderRadius: 999,
          fontSize: 11,
          fontWeight: 700,
          background: 'rgba(255,255,255,0.10)',
          color: s.text,
        }}
      >
        {band === 'hot' && <Flame size={11} />}
        {s.label}
      </span>
    </div>
  );
}

/** Per-band presentation for the customer-health badge (mirrors the CS view). */
const healthBandStyle: Record<HealthBand, { bg: string; border: string; text: string; label: string }> = {
  'at-risk': {
    bg: 'linear-gradient(135deg, rgba(255,0,255,0.22), rgba(226,57,57,0.16))',
    border: 'rgba(255,0,255,0.45)',
    text: '#ffffff',
    label: 'At risk',
  },
  watch: {
    bg: 'rgba(253,173,1,0.14)',
    border: 'rgba(253,173,1,0.40)',
    text: '#ffcf7a',
    label: 'Watch',
  },
  healthy: {
    bg: 'rgba(34,197,94,0.14)',
    border: 'rgba(34,197,94,0.40)',
    text: '#7ee2a8',
    label: 'Healthy',
  },
};

/**
 * Customer-health badge for the detail Hero — the live-account counterpart to
 * the lead score. Computed on demand from the customer's own record (never
 * stored), it shows the 0–100 health, its band, and the risk factors as a
 * tooltip so a CS owner sees *why* the moment they open the account.
 */
function CustomerHealthBadge({ person }: { person: Person }) {
  const { score, band, factors } = scoreCustomerHealth(person);
  const s = healthBandStyle[band];
  const tip = factors
    .map((f) => `${f.label}: ${f.points > 0 ? '+' : ''}${f.points}`)
    .join('\n');
  return (
    <div
      title={`Customer health ${score} (${s.label})\n${tip}`}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '8px 14px',
        borderRadius: 14,
        background: s.bg,
        border: `1px solid ${s.border}`,
      }}
    >
      <div style={{ textAlign: 'right' }}>
        <div className="t-meta" style={{ lineHeight: 1.1 }}>Health</div>
        <div style={{ fontSize: 22, fontWeight: 800, lineHeight: 1.05, color: s.text }}>
          {score}
        </div>
      </div>
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 4,
          padding: '2px 8px',
          borderRadius: 999,
          fontSize: 11,
          fontWeight: 700,
          background: 'rgba(255,255,255,0.10)',
          color: s.text,
        }}
      >
        {band === 'at-risk' && <AlertTriangle size={11} />}
        {s.label}
      </span>
    </div>
  );
}

// --- Next-step / follow-up strip -------------------------------------------

function NextStepStrip({ person }: { person: Person }) {
  const overdue = isFollowUpOverdue(person);
  const followLabel = person.followUpDate
    ? `${fmtDate(person.followUpDate)}${overdue ? ' · overdue' : ''}`
    : undefined;
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
      <div
        style={{
          padding: '10px 12px',
          borderRadius: 12,
          background: 'rgba(255,255,255,0.04)',
          border: '1px solid rgba(255,255,255,0.06)',
        }}
      >
        <div
          className="t-meta"
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginBottom: 2 }}
        >
          <Send size={11} /> Next step
        </div>
        <div className="t-card-title" style={{ color: person.nextStep ? C.text : C.textDim }}>
          {person.nextStep ?? 'Nothing queued'}
        </div>
      </div>
      <div
        style={{
          padding: '10px 12px',
          borderRadius: 12,
          background: overdue ? 'rgba(253,173,1,0.10)' : 'rgba(255,255,255,0.04)',
          border: `1px solid ${overdue ? 'rgba(253,173,1,0.35)' : 'rgba(255,255,255,0.06)'}`,
        }}
      >
        <div
          className="t-meta"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            marginBottom: 2,
            color: overdue ? C.orangeWarm : undefined,
          }}
        >
          <CalendarClock size={11} /> Follow-up
        </div>
        <div
          className="t-card-title"
          style={{ color: followLabel ? (overdue ? C.orangeWarm : C.text) : C.textDim }}
        >
          {followLabel ?? 'No date set'}
        </div>
      </div>
    </div>
  );
}

// --- Activity timeline -----------------------------------------------------

const ACTIVITY_KINDS: {
  key: PersonActivity['kind'];
  label: string;
  icon: LucideIcon;
  color: string;
}[] = [
  { key: 'note', label: 'Note', icon: StickyNote, color: C.textMuted },
  { key: 'call', label: 'Call', icon: Phone, color: C.green },
  { key: 'email', label: 'Email', icon: AtSign, color: C.blueElectric },
  { key: 'meeting', label: 'Meeting', icon: Users, color: C.magentaSoft },
  { key: 'task', label: 'Task', icon: Check, color: C.orangeWarm },
  { key: 'stage', label: 'Stage', icon: Hash, color: C.magenta },
  { key: 'system', label: 'System', icon: Bot, color: C.blueElectric },
];

const activityMeta = (kind: PersonActivity['kind']) =>
  ACTIVITY_KINDS.find((k) => k.key === kind) ?? ACTIVITY_KINDS[0];

function ActivityTimeline({
  person,
  canMutate,
  onLog,
  onSyncMeet,
  onSyncEmail,
}: {
  person: Person;
  canMutate: boolean;
  onLog: (kind: PersonActivity['kind'], text: string) => Promise<void>;
  onSyncMeet: (person: Person) => Promise<number>;
  onSyncEmail: (person: Person) => Promise<number>;
}) {
  const [kind, setKind] = useState<PersonActivity['kind']>('note');
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  // `syncing` is a discriminated busy flag so the two sync buttons don't
  // both spin when one runs.
  const [syncing, setSyncing] = useState<null | 'meet' | 'email'>(null);
  const [syncMsg, setSyncMsg] = useState<string | null>(null);

  const syncMeet = async () => {
    if (syncing) return;
    setSyncing('meet');
    setSyncMsg(null);
    try {
      const n = await onSyncMeet(person);
      setSyncMsg(n > 0 ? `Synced ${n} Meet note${n === 1 ? '' : 's'}` : 'No new Meet notes');
    } catch {
      setSyncMsg('Sync failed');
    } finally {
      setSyncing(null);
    }
  };

  const syncEmail = async () => {
    if (syncing) return;
    setSyncing('email');
    setSyncMsg(null);
    try {
      const n = await onSyncEmail(person);
      setSyncMsg(n > 0 ? `Synced ${n} email${n === 1 ? '' : 's'}` : 'No new emails');
    } catch {
      setSyncMsg('Sync failed');
    } finally {
      setSyncing(null);
    }
  };

  const entries = useMemo(
    () =>
      [...(person.activity ?? [])].sort(
        (a, b) => Date.parse(b.at) - Date.parse(a.at),
      ),
    [person.activity],
  );

  const submit = async () => {
    const body = text.trim();
    if (!body || busy) return;
    setBusy(true);
    try {
      await onLog(kind, body);
      setText('');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <div
        className="t-eyebrow"
        style={{ marginBottom: 6, display: 'flex', alignItems: 'center', gap: 6 }}
      >
        <Activity size={11} /> Activity
        {entries.length > 0 && (
          <span className="t-meta" style={{ color: C.textDim }}>· {entries.length}</span>
        )}
        {canMutate && (
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
            {syncMsg && (
              <span className="t-meta" style={{ color: C.textDim, textTransform: 'none' }}>
                {syncMsg}
              </span>
            )}
            <button
              className="glass-btn"
              onClick={() => void syncEmail()}
              disabled={syncing !== null}
              title="Pull recent Gmail threads into this timeline"
              style={{ flexShrink: 0 }}
            >
              {syncing === 'email' ? (
                <Loader2 size={12} style={{ animation: 'auth-spin 0.8s linear infinite' }} />
              ) : (
                <Mail size={12} />
              )}
              {syncing === 'email' ? 'Syncing…' : 'Sync email'}
            </button>
            <button
              className="glass-btn"
              onClick={() => void syncMeet()}
              disabled={syncing !== null}
              title="Pull recent Google Meet notes into this timeline"
              style={{ flexShrink: 0 }}
            >
              {syncing === 'meet' ? (
                <Loader2 size={12} style={{ animation: 'auth-spin 0.8s linear infinite' }} />
              ) : (
                <Video size={12} />
              )}
              {syncing === 'meet' ? 'Syncing…' : 'Sync Meet notes'}
            </button>
          </div>
        )}
      </div>

      {/* Quick-log composer — editors only. */}
      {canMutate && (
        <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
          <select
            className="glass-input"
            value={kind}
            onChange={(e) => setKind(e.target.value as PersonActivity['kind'])}
            style={{ width: 110, flexShrink: 0, fontFamily: 'Manrope, sans-serif' }}
          >
            {ACTIVITY_KINDS.filter((k) => k.key !== 'system').map((k) => (
              <option key={k.key} value={k.key}>
                {k.label}
              </option>
            ))}
          </select>
          <input
            className="glass-input"
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                void submit();
              }
            }}
            placeholder="Log a touch — call, email, meeting…"
            style={{ flex: 1, fontSize: 12 }}
          />
          <button
            className="glass-btn glass-btn--primary"
            onClick={() => void submit()}
            disabled={busy || !text.trim()}
            style={{ flexShrink: 0 }}
          >
            <Send size={13} /> Log
          </button>
        </div>
      )}

      {entries.length === 0 ? (
        <span className="t-meta">No activity logged yet.</span>
      ) : (
        <div style={{ display: 'grid', gap: 2 }}>
          {entries.map((e, i) => {
            const m = activityMeta(e.kind);
            const Icon = m.icon;
            const last = i === entries.length - 1;
            return (
              <div key={e.id} style={{ display: 'flex', gap: 10 }}>
                {/* Rail */}
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    flexShrink: 0,
                  }}
                >
                  <span
                    style={{
                      width: 22,
                      height: 22,
                      borderRadius: '50%',
                      display: 'inline-grid',
                      placeItems: 'center',
                      background: 'rgba(255,255,255,0.05)',
                      border: '1px solid rgba(255,255,255,0.10)',
                      color: m.color,
                    }}
                  >
                    <Icon size={11} />
                  </span>
                  {!last && (
                    <span
                      style={{
                        width: 1,
                        flex: 1,
                        minHeight: 8,
                        background: 'rgba(255,255,255,0.10)',
                      }}
                    />
                  )}
                </div>
                {/* Body */}
                <div style={{ paddingBottom: 12, minWidth: 0 }}>
                  <div className="t-body" style={{ color: C.text }}>{e.text}</div>
                  <div className="t-meta" style={{ color: C.textDim }}>
                    {m.label} · {e.actor ?? 'You'} · {fmtWhen(e.at)}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// --- Pre-meeting brief -----------------------------------------------------

/**
 * The pre-meeting brief surface on a contact/prospect: one click pulls
 * the latest meeting notes (real Google Meet when the worker is live, the
 * labelled simulation otherwise) and composes a deterministic one-pager —
 * deal state, history, open items, talking points. "Copy" puts a markdown
 * version on the clipboard for a doc or a Slack message.
 */
function PreMeetingBriefPanel({
  person,
  allPeople,
}: {
  person: Person;
  allPeople: Person[];
}) {
  const [brief, setBrief] = useState<PreMeetingBrief | null>(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  const generate = async () => {
    if (loading) return;
    setLoading(true);
    try {
      // Read-only pull (does NOT write to the timeline — that's "Sync").
      const meetNotes = await pullMeetingNotes({
        name: person.name,
        company: person.company,
        email: person.email,
      });
      setBrief(composePreMeetingBrief({ person, meetNotes, roster: allPeople }));
    } catch {
      setBrief(composePreMeetingBrief({ person, meetNotes: [], roster: allPeople }));
    } finally {
      setLoading(false);
    }
  };

  const copy = async () => {
    if (!brief) return;
    try {
      await navigator.clipboard.writeText(briefToMarkdown(person, brief));
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard may be blocked in some contexts — silent no-op */
    }
  };

  return (
    <div
      style={{
        padding: '12px 14px',
        borderRadius: 12,
        background: 'rgba(255,0,255,0.05)',
        border: '1px solid rgba(255,0,255,0.16)',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          marginBottom: brief ? 12 : 0,
        }}
      >
        <div
          className="t-eyebrow"
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
        >
          <ClipboardList size={11} /> Pre-meeting brief
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          {brief && (
            <button className="glass-btn" onClick={() => void copy()} style={{ flexShrink: 0 }}>
              {copied ? <Check size={12} /> : <StickyNote size={12} />}
              {copied ? 'Copied' : 'Copy'}
            </button>
          )}
          <button
            className="glass-btn glass-btn--primary"
            onClick={() => void generate()}
            disabled={loading}
            style={{ flexShrink: 0 }}
          >
            {loading ? (
              <Loader2 size={12} style={{ animation: 'auth-spin 0.8s linear infinite' }} />
            ) : (
              <Sparkles size={12} />
            )}
            {loading ? 'Building…' : brief ? 'Refresh' : 'Generate brief'}
          </button>
        </div>
      </div>

      {brief && (
        <div style={{ display: 'grid', gap: 12 }}>
          <BriefSection title="Deal state" rows={brief.dealState} />
          <BriefSection title="History" rows={brief.history} />
          <BriefSection title="Open items" rows={brief.openItems} />
          <BriefSection title="Talking points" rows={brief.talkingPoints} accent />
        </div>
      )}
    </div>
  );
}

function BriefSection({
  title,
  rows,
  accent,
}: {
  title: string;
  rows: string[];
  accent?: boolean;
}) {
  if (rows.length === 0) return null;
  return (
    <div>
      <div
        className="t-meta"
        style={{ color: accent ? C.magenta : C.textMuted, marginBottom: 4 }}
      >
        {title}
      </div>
      <ul style={{ margin: 0, paddingLeft: 16, display: 'grid', gap: 3 }}>
        {rows.map((r, i) => (
          <li key={i} className="t-body" style={{ color: C.text, lineHeight: 1.45 }}>
            {r}
          </li>
        ))}
      </ul>
    </div>
  );
}

// --- Detail modal ----------------------------------------------------------

/**
 * PersonDetailModal — the click-through overlay for a contact/prospect.
 *
 * Mirrors the Accounts CRM experience: clicking a list row opens a
 * focused modal (same `account-modal` chrome) rather than a side panel,
 * so "clicking into" a person is obvious. Escape and backdrop-click
 * close it; focus is trapped while open.
 */
function PersonDetailModal({
  onClose,
  children,
}: {
  onClose: () => void;
  children: ReactNode;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  useFocusTrap(containerRef, true);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return createPortal(
    <div
      className="account-modal-backdrop"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      data-menu-portal="true"
    >
      <div
        ref={containerRef}
        className="account-modal-wrap"
        role="dialog"
        aria-modal="true"
        tabIndex={-1}
        style={{ width: 'min(760px, 100%)' }}
      >
        <GlassPanel variant="hero" className="account-modal" animate>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 4 }}>
            <button
              type="button"
              className="glass-btn"
              onClick={onClose}
              aria-label="Close"
            >
              <X size={13} />
            </button>
          </div>
          <div
            style={{
              maxHeight: 'calc(100vh - 180px)',
              overflowY: 'auto',
              paddingRight: 4,
            }}
          >
            {children}
          </div>
        </GlassPanel>
      </div>
    </div>,
    document.body,
  );
}

// --- Detail read -----------------------------------------------------------

function DetailReadView({
  person,
  allPeople,
  canMutate,
  onEdit,
  onDelete,
  onLogActivity,
  onEnrich,
  onSyncMeet,
  onSyncEmail,
}: {
  person: Person;
  allPeople: Person[];
  canMutate: boolean;
  onEdit: () => void;
  onDelete: () => void;
  onLogActivity: (kind: PersonActivity['kind'], text: string) => Promise<void>;
  onEnrich: (person: Person) => Promise<void>;
  onSyncMeet: (person: Person) => Promise<number>;
  onSyncEmail: (person: Person) => Promise<number>;
}) {
  const scored = person.category === 'customer' || person.category === 'prospect';
  return (
    <div style={{ display: 'grid', gap: 18 }}>
      {/* Hero */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
        <Avatar name={person.name} category={person.category} size={56} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <h2 className="t-section-title" style={{ margin: 0 }}>{person.name}</h2>
          <div className="t-body" style={{ color: C.textMuted, marginTop: 2 }}>
            {[person.title, person.company].filter(Boolean).join(' · ') || '—'}
          </div>
        </div>
        {person.category === 'customer' && <CustomerHealthBadge person={person} />}
        {person.category === 'prospect' && <LeadScoreBadge person={person} size="lg" />}
        {canMutate && (
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="glass-btn" onClick={onEdit}>
              Edit
            </button>
            <button className="glass-btn" onClick={onDelete} title="Delete">
              <Trash2 size={13} />
            </button>
          </div>
        )}
      </div>

      {/* Next step + follow-up — the always-visible "what now" strip. */}
      {scored && <NextStepStrip person={person} />}

      {/* Pre-meeting brief — the 60-second one-pager before a call. */}
      {scored && <PreMeetingBriefPanel person={person} allPeople={allPeople} />}

      {/* Contact */}
      <FieldGrid>
        <Field icon={AtSign} label="Email" value={person.email} />
        <Field icon={Phone} label="Phone" value={person.phone} />
        <Field icon={Briefcase} label="Title" value={person.title} />
        <Field icon={Building2} label="Company" value={person.company} />
      </FieldGrid>

      {/* Category-specific */}
      <CategorySpecific person={person} />

      {/* Web presence — links + the enrichment evaluation (prospects). */}
      {(person.category === 'prospect' || person.category === 'customer') && (
        <WebPresence person={person} canMutate={canMutate} onEnrich={onEnrich} />
      )}

      {/* Engagement — deliverables, billing schedule, points of contact. */}
      {person.category === 'customer' && <CustomerEngagement person={person} />}

      {/* Tags */}
      <div>
        <div className="t-eyebrow" style={{ marginBottom: 6 }}>Tags</div>
        {person.tags.length === 0 ? (
          <span className="t-meta">No tags yet.</span>
        ) : (
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {person.tags.map((t) => (
              <span key={t} className="glass-pill">
                <TagIcon size={10} /> {t}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Notes */}
      <div>
        <div className="t-eyebrow" style={{ marginBottom: 6 }}>Notes</div>
        {person.notes ? (
          <p className="t-body" style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
            {person.notes}
          </p>
        ) : (
          <span className="t-meta">No notes yet.</span>
        )}
      </div>

      {/* Activity timeline — the relationship history + quick-log. */}
      {scored && (
        <ActivityTimeline
          person={person}
          canMutate={canMutate}
          onLog={onLogActivity}
          onSyncMeet={onSyncMeet}
          onSyncEmail={onSyncEmail}
        />
      )}

      {/* Documents — proposals & contracts for prospects */}
      {person.category === 'prospect' && (
        <div>
          <div className="t-eyebrow" style={{ marginBottom: 6 }}>Documents</div>
          <DocumentsPanel
            entityType="person"
            entityId={person.id}
            entityName={person.company || person.name}
            contactName={person.name}
            ownerName={person.ownerName}
            defaultValue={person.estimatedValue}
          />
        </div>
      )}

      {/* Tasks — to-dos tied to this contact (customers & prospects) */}
      {(person.category === 'customer' || person.category === 'prospect') && (
        <div>
          <div className="t-eyebrow" style={{ marginBottom: 6 }}>Tasks</div>
          <TasksPanel entityType="person" entityId={person.id} entityName={person.name} />
        </div>
      )}

      {/* Bookkeeping */}
      <div className="t-meta" style={{ paddingTop: 10, borderTop: '1px solid rgba(255,255,255,0.06)' }}>
        Created {fmtDate(person.createdAt)} · Updated {fmtDate(person.updatedAt)}
      </div>
    </div>
  );
}

function CategorySpecific({ person }: { person: Person }) {
  if (person.category === 'customer') {
    return (
      <FieldGrid>
        <Field icon={Hash} label="Status" value={person.status} />
        <Field
          icon={DollarSign}
          label="Account value"
          value={person.accountValue !== undefined ? fmtUsd(person.accountValue) : undefined}
        />
        <Field icon={Calendar} label="Contract start" value={person.contractStart} />
        <Field icon={CalendarClock} label="Renewal" value={person.renewalDate} />
        <Field icon={UserCircle2} label="Owner" value={person.ownerName} />
      </FieldGrid>
    );
  }
  if (person.category === 'prospect') {
    return (
      <FieldGrid>
        <Field icon={Hash} label="Stage" value={person.stage} />
        <Field icon={Target} label="Source" value={person.source} />
        <Field
          icon={DollarSign}
          label="Estimated value"
          value={person.estimatedValue !== undefined ? fmtUsd(person.estimatedValue) : undefined}
        />
        <Field icon={Calendar} label="Last touch" value={person.lastTouch} />
      </FieldGrid>
    );
  }
  if (person.category === 'staff') {
    return (
      <FieldGrid>
        <Field icon={Hash} label="Role" value={person.role} />
        <Field icon={Users} label="Team" value={person.team} />
        <Field icon={Calendar} label="Start date" value={person.startDate} />
        <Field icon={MapPin} label="Location" value={person.location} />
      </FieldGrid>
    );
  }
  return null;
}

// --- Web presence + enrichment ---------------------------------------------

/**
 * LinkedIn / website links plus the prospect-enrichment evaluation.
 * The "Enrich" button calls the enrichment seam (live worker when
 * configured, deterministic simulation otherwise) and persists the read
 * back onto the person, so the summary + signals + fit score render here
 * on the next subscription tick.
 */
function WebPresence({
  person,
  canMutate,
  onEnrich,
}: {
  person: Person;
  canMutate: boolean;
  onEnrich: (person: Person) => Promise<void>;
}) {
  const [busy, setBusy] = useState(false);
  const enrichment = person.enrichment;
  const pending = busy || enrichment?.status === 'pending';
  const isProspect = person.category === 'prospect' && canMutate;

  const run = async () => {
    setBusy(true);
    try {
      await onEnrich(person);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 6,
        }}
      >
        <div className="t-eyebrow">Web presence</div>
        {isProspect && (
          <button className="glass-btn" onClick={run} disabled={pending}>
            {pending ? (
              <Loader2 size={13} style={{ animation: 'auth-spin 0.8s linear infinite' }} />
            ) : (
              <Sparkles size={13} />
            )}
            {pending ? 'Enriching…' : enrichment ? 'Re-enrich' : 'Enrich'}
          </button>
        )}
      </div>

      <FieldGrid>
        <LinkField icon={Linkedin} label="LinkedIn" url={person.linkedinUrl} />
        <LinkField icon={Globe} label="Website" url={person.website} />
      </FieldGrid>

      {enrichment && enrichment.status !== 'pending' && (
        <div
          style={{
            marginTop: 10,
            padding: '12px 14px',
            borderRadius: 12,
            background: 'rgba(255,255,255,0.04)',
            border: '1px solid rgba(255,255,255,0.08)',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              marginBottom: 6,
            }}
          >
            <span
              className="t-meta"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: C.magentaSoft }}
            >
              <Sparkles size={11} /> AI evaluation
            </span>
            {enrichment.fitScore !== undefined && (
              <span className="glass-pill" style={{ color: C.text }}>
                Fit {enrichment.fitScore}/100
              </span>
            )}
          </div>
          {enrichment.summary && (
            <p className="t-body" style={{ margin: '0 0 8px', color: C.text }}>
              {enrichment.summary}
            </p>
          )}
          {enrichment.signals && enrichment.signals.length > 0 && (
            <div style={{ display: 'grid', gap: 4 }}>
              {enrichment.signals.map((s, i) => (
                <div
                  key={i}
                  className="t-meta"
                  style={{ display: 'flex', gap: 6, color: C.textMuted }}
                >
                  <span style={{ color: C.magentaSoft }}>•</span> {s}
                </div>
              ))}
            </div>
          )}
          {enrichment.evaluatedAt && (
            <div className="t-meta" style={{ marginTop: 8, color: C.textDim }}>
              Evaluated {fmtWhen(enrichment.evaluatedAt)}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/** A labelled field that renders a clickable link, or "—" when empty. */
function LinkField({ icon: Icon, label, url }: { icon: LucideIcon; label: string; url?: string }) {
  const href = url ? (/^https?:\/\//i.test(url) ? url : `https://${url}`) : undefined;
  return (
    <div
      style={{
        padding: '10px 12px',
        borderRadius: 12,
        background: 'rgba(255,255,255,0.04)',
        border: '1px solid rgba(255,255,255,0.06)',
      }}
    >
      <div
        className="t-meta"
        style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginBottom: 2 }}
      >
        <Icon size={11} /> {label}
      </div>
      {href ? (
        <a
          href={href}
          target="_blank"
          rel="noreferrer noopener"
          className="t-card-title"
          style={{
            color: C.magentaSoft,
            textDecoration: 'none',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            display: 'block',
          }}
        >
          {url}
        </a>
      ) : (
        <div className="t-card-title" style={{ color: C.textDim }}>—</div>
      )}
    </div>
  );
}

// --- Customer engagement (read) --------------------------------------------

const DELIVERABLE_TONE: Record<PersonDeliverable['status'], string> = {
  planned: C.textMuted,
  'in-progress': C.blueElectric,
  delivered: C.green,
  blocked: C.orangeWarm,
};

const BILLING_TONE: Record<NonNullable<BillingDate['status']>, string> = {
  scheduled: C.textMuted,
  invoiced: C.blueElectric,
  paid: C.green,
  overdue: C.orangeWarm,
};

/**
 * Customer engagement detail — the three things that make this a real
 * post-sale CRM: what we owe (deliverables), when we bill (billing
 * schedule), and who we talk to (points of contact). Read-only render;
 * editing happens in `CustomerEngagementEdit`.
 */
function CustomerEngagement({ person }: { person: Person }) {
  const deliverables = person.deliverables ?? [];
  const billing = person.billingDates ?? [];
  const pocs = person.pocs ?? [];

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <section>
        <div
          className="t-eyebrow"
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginBottom: 6 }}
        >
          <ClipboardList size={11} /> Deliverables
        </div>
        {deliverables.length === 0 ? (
          <span className="t-meta">No deliverables tracked yet.</span>
        ) : (
          <div style={{ display: 'grid', gap: 6 }}>
            {deliverables.map((d) => (
              <div key={d.id} className="glass-row" style={{ alignItems: 'center' }}>
                <span
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: 99,
                    background: DELIVERABLE_TONE[d.status],
                    flexShrink: 0,
                  }}
                />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="t-card-title" style={{ color: C.text }}>{d.title}</div>
                  {(d.owner || d.dueDate) && (
                    <div className="t-meta" style={{ color: C.textMuted }}>
                      {[d.owner, d.dueDate && `due ${d.dueDate}`].filter(Boolean).join(' · ')}
                    </div>
                  )}
                </div>
                <span className="glass-pill" style={{ color: DELIVERABLE_TONE[d.status] }}>
                  {d.status}
                </span>
              </div>
            ))}
          </div>
        )}
      </section>

      <section>
        <div
          className="t-eyebrow"
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginBottom: 6 }}
        >
          <Receipt size={11} /> Billing schedule
        </div>
        {billing.length === 0 ? (
          <span className="t-meta">No billing milestones scheduled.</span>
        ) : (
          <div style={{ display: 'grid', gap: 6 }}>
            {[...billing]
              .sort((a, b) => a.date.localeCompare(b.date))
              .map((b) => (
                <div key={b.id} className="glass-row" style={{ alignItems: 'center' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="t-card-title" style={{ color: C.text }}>{b.label}</div>
                    <div className="t-meta" style={{ color: C.textMuted }}>{b.date}</div>
                  </div>
                  {b.amount !== undefined && (
                    <span className="t-card-title" style={{ color: C.text }}>
                      {fmtUsd(b.amount)}
                    </span>
                  )}
                  {b.status && (
                    <span className="glass-pill" style={{ color: BILLING_TONE[b.status] }}>
                      {b.status}
                    </span>
                  )}
                </div>
              ))}
          </div>
        )}
      </section>

      <section>
        <div
          className="t-eyebrow"
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginBottom: 6 }}
        >
          <Users size={11} /> Points of contact
        </div>
        {pocs.length === 0 ? (
          <span className="t-meta">No additional contacts on file.</span>
        ) : (
          <div style={{ display: 'grid', gap: 6 }}>
            {pocs.map((p) => (
              <div key={p.id} className="glass-row" style={{ alignItems: 'center' }}>
                <Avatar name={p.name} category="customer" size={32} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    className="t-card-title"
                    style={{ color: C.text, display: 'flex', alignItems: 'center', gap: 6 }}
                  >
                    {p.name}
                    {p.primary && (
                      <span
                        className="glass-pill glass-pill--magenta"
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
                      >
                        <Star size={9} /> Primary
                      </span>
                    )}
                  </div>
                  <div className="t-meta" style={{ color: C.textMuted }}>
                    {[p.title, p.email, p.phone].filter(Boolean).join(' · ') || '—'}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

// --- Detail edit -----------------------------------------------------------

function DetailEditView({
  draft,
  onChange,
  onCancel,
  onSave,
  onDelete,
  ownerOptions,
}: {
  draft: Person;
  onChange: (next: Person) => void;
  onCancel: () => void;
  onSave: () => void;
  onDelete: () => void;
  ownerOptions: string[];
}) {
  const set = <K extends keyof Person>(key: K, value: Person[K]) =>
    onChange({ ...draft, [key]: value });

  return (
    <div style={{ display: 'grid', gap: 18 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
        <Avatar name={draft.name || '?'} category={draft.category} size={56} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <input
            className="glass-input"
            value={draft.name}
            onChange={(e) => set('name', e.target.value)}
            placeholder="Full name"
            style={{ fontSize: 15, fontWeight: 600 }}
          />
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="glass-btn" onClick={onCancel}>
            <X size={13} /> Cancel
          </button>
          <button className="glass-btn glass-btn--primary" onClick={onSave}>
            <Check size={13} /> Save
          </button>
        </div>
      </div>

      <FieldGrid>
        <EditField label="Email" value={draft.email} onChange={(v) => set('email', v)} />
        <EditField label="Phone" value={draft.phone} onChange={(v) => set('phone', v)} />
        <EditField label="Title" value={draft.title} onChange={(v) => set('title', v)} />
        <EditField label="Company" value={draft.company} onChange={(v) => set('company', v)} />
      </FieldGrid>

      <CategorySpecificEdit draft={draft} set={set} ownerOptions={ownerOptions} />

      {(draft.category === 'customer' || draft.category === 'prospect') && (
        <FieldGrid>
          <EditField
            label="LinkedIn URL"
            value={draft.linkedinUrl}
            onChange={(v) => set('linkedinUrl', v)}
            placeholder="linkedin.com/in/…"
          />
          <EditField
            label="Website"
            value={draft.website}
            onChange={(v) => set('website', v)}
            placeholder="company.com"
          />
        </FieldGrid>
      )}

      {(draft.category === 'customer' || draft.category === 'prospect') && (
        <FieldGrid>
          <EditField
            label="Next step"
            value={draft.nextStep}
            onChange={(v) => set('nextStep', v)}
            placeholder="e.g. Send pricing one-pager"
          />
          <div>
            <div className="t-eyebrow" style={{ marginBottom: 4 }}>Follow-up date</div>
            <input
              type="date"
              className="glass-input"
              value={draft.followUpDate ?? ''}
              onChange={(e) => set('followUpDate', e.target.value || undefined)}
              style={{ width: '100%', fontFamily: 'Manrope, sans-serif' }}
            />
          </div>
        </FieldGrid>
      )}

      {draft.category === 'customer' && (
        <CustomerEngagementEdit draft={draft} set={set} />
      )}

      <div>
        <div className="t-eyebrow" style={{ marginBottom: 6 }}>Tags (comma-separated)</div>
        <input
          className="glass-input"
          value={draft.tags.join(', ')}
          onChange={(e) =>
            set(
              'tags',
              e.target.value
                .split(',')
                .map((t) => t.trim())
                .filter(Boolean),
            )
          }
          placeholder="enterprise, fast-track, expansion"
        />
      </div>

      <div>
        <div className="t-eyebrow" style={{ marginBottom: 6 }}>Notes</div>
        <textarea
          className="glass-input"
          value={draft.notes ?? ''}
          onChange={(e) => set('notes', e.target.value)}
          rows={5}
          placeholder="Anything worth remembering — context, last conversation, next step…"
          style={{
            width: '100%',
            padding: '10px 12px',
            fontFamily: 'Manrope, sans-serif',
            lineHeight: 1.5,
          }}
        />
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <button className="glass-btn" onClick={onDelete}>
          <Trash2 size={13} /> Delete
        </button>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="glass-btn" onClick={onCancel}>
            <X size={13} /> Cancel
          </button>
          <button className="glass-btn glass-btn--primary" onClick={onSave}>
            <Check size={13} /> Save
          </button>
        </div>
      </div>
    </div>
  );
}

type SetFn = <K extends keyof Person>(key: K, value: Person[K]) => void;

function CategorySpecificEdit({
  draft,
  set,
  ownerOptions,
}: {
  draft: Person;
  set: SetFn;
  ownerOptions: string[];
}) {
  // Owner options must always include the current value, even if it's a
  // legacy free-text owner not in the AppUsers list, so the select never
  // silently blanks it.
  const ownerOpts =
    draft.ownerName && !ownerOptions.includes(draft.ownerName)
      ? [...ownerOptions, draft.ownerName]
      : ownerOptions;
  if (draft.category === 'customer') {
    return (
      <FieldGrid>
        <EditSelect
          label="Status"
          value={draft.status ?? ''}
          options={['', 'active', 'paused', 'churned']}
          onChange={(v) => set('status', (v || undefined) as Person['status'])}
        />
        <EditField
          label="Account value (USD)"
          value={draft.accountValue !== undefined ? String(draft.accountValue) : ''}
          onChange={(v) => set('accountValue', v ? Number(v) : undefined)}
        />
        <EditField
          label="Contract start"
          value={draft.contractStart}
          onChange={(v) => set('contractStart', v)}
          placeholder="YYYY-MM-DD"
        />
        <EditField
          label="Renewal date"
          value={draft.renewalDate}
          onChange={(v) => set('renewalDate', v)}
          placeholder="YYYY-MM-DD"
        />
        <EditSelect
          label="Owner"
          value={draft.ownerName ?? ''}
          options={ownerOpts}
          onChange={(v) => set('ownerName', v || undefined)}
        />
      </FieldGrid>
    );
  }
  if (draft.category === 'prospect') {
    const closing =
      draft.stage === 'closed-won' || draft.stage === 'closed-lost';
    const reasonOpts =
      draft.stage === 'closed-won' || draft.stage === 'closed-lost'
        ? reasonsForStage(draft.stage)
        : [];
    const reasonValue =
      draft.winLossReason && !reasonOpts.includes(draft.winLossReason)
        ? [...reasonOpts, draft.winLossReason]
        : [...reasonOpts];
    return (
      <>
        <FieldGrid>
          <EditSelect
            label="Stage"
            value={draft.stage ?? ''}
            options={['', 'new', 'qualified', 'proposal', 'closed-won', 'closed-lost']}
            onChange={(v) => set('stage', (v || undefined) as Person['stage'])}
          />
          <EditSelect
            label="Source"
            value={draft.source ?? ''}
            options={
              draft.source && !SOURCE_OPTIONS.includes(draft.source as (typeof SOURCE_OPTIONS)[number])
                ? [...SOURCE_OPTIONS, draft.source]
                : [...SOURCE_OPTIONS]
            }
            onChange={(v) => set('source', v || undefined)}
          />
          <EditField
            label="Estimated value (USD)"
            value={draft.estimatedValue !== undefined ? String(draft.estimatedValue) : ''}
            onChange={(v) => set('estimatedValue', v ? Number(v) : undefined)}
          />
          <EditSelect
            label="Owner"
            value={draft.ownerName ?? ''}
            options={ownerOpts}
            onChange={(v) => set('ownerName', v || undefined)}
          />
        </FieldGrid>
        {closing && (
          <div
            style={{
              marginTop: 12,
              padding: 12,
              borderRadius: 12,
              background:
                draft.stage === 'closed-won'
                  ? 'rgba(16,185,129,0.06)'
                  : 'rgba(244,63,94,0.06)',
              border: `1px solid ${
                draft.stage === 'closed-won'
                  ? 'rgba(16,185,129,0.18)'
                  : 'rgba(244,63,94,0.18)'
              }`,
            }}
          >
            <div className="t-eyebrow" style={{ marginBottom: 8 }}>
              {draft.stage === 'closed-won' ? 'Why we won' : 'Why we lost'} — feeds
              win-loss intelligence
            </div>
            <FieldGrid>
              <EditSelect
                label="Reason"
                value={draft.winLossReason ?? ''}
                options={['', ...reasonValue]}
                onChange={(v) => set('winLossReason', v || undefined)}
              />
              <EditField
                label="Note (optional)"
                value={draft.winLossNote}
                onChange={(v) => set('winLossNote', v)}
                placeholder="The deal's story in a line"
              />
            </FieldGrid>
          </div>
        )}
      </>
    );
  }
  if (draft.category === 'staff') {
    return (
      <FieldGrid>
        <EditField label="Role" value={draft.role} onChange={(v) => set('role', v)} />
        <EditField label="Team" value={draft.team} onChange={(v) => set('team', v)} />
        <EditField
          label="Start date"
          value={draft.startDate}
          onChange={(v) => set('startDate', v)}
          placeholder="YYYY-MM-DD"
        />
        <EditField
          label="Location"
          value={draft.location}
          onChange={(v) => set('location', v)}
        />
      </FieldGrid>
    );
  }
  return null;
}

// --- Customer engagement (edit) --------------------------------------------

/**
 * Inline editors for a customer's deliverables, billing schedule, and
 * points of contact. Each list supports add / edit / remove and writes
 * straight back onto the draft via `set`, so the parent save path
 * persists them with the rest of the record — no separate adapter.
 */
function CustomerEngagementEdit({ draft, set }: { draft: Person; set: SetFn }) {
  const deliverables = draft.deliverables ?? [];
  const billing = draft.billingDates ?? [];
  const pocs = draft.pocs ?? [];

  const patchDeliverable = (id: string, patch: Partial<PersonDeliverable>) =>
    set('deliverables', deliverables.map((d) => (d.id === id ? { ...d, ...patch } : d)));
  const patchBilling = (id: string, patch: Partial<BillingDate>) =>
    set('billingDates', billing.map((b) => (b.id === id ? { ...b, ...patch } : b)));
  const patchPoc = (id: string, patch: Partial<ContactPOC>) =>
    set(
      'pocs',
      pocs.map((p) =>
        // Keep a single primary: clearing others when one is set primary.
        p.id === id
          ? { ...p, ...patch }
          : patch.primary
            ? { ...p, primary: false }
            : p,
      ),
    );

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      {/* Deliverables */}
      <EditListSection
        icon={ClipboardList}
        label="Deliverables"
        onAdd={() =>
          set('deliverables', [
            ...deliverables,
            { id: uuid(), title: '', status: 'planned' },
          ])
        }
      >
        {deliverables.map((d) => (
          <div key={d.id} className="glass-row" style={{ alignItems: 'flex-start', gap: 8 }}>
            <div style={{ flex: 1, display: 'grid', gap: 6 }}>
              <input
                className="glass-input"
                value={d.title}
                onChange={(e) => patchDeliverable(d.id, { title: e.target.value })}
                placeholder="Deliverable title"
              />
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 6 }}>
                <select
                  className="glass-input"
                  value={d.status}
                  onChange={(e) =>
                    patchDeliverable(d.id, { status: e.target.value as PersonDeliverable['status'] })
                  }
                  style={{ fontFamily: 'Manrope, sans-serif' }}
                >
                  {(['planned', 'in-progress', 'delivered', 'blocked'] as const).map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
                <input
                  className="glass-input"
                  value={d.dueDate ?? ''}
                  onChange={(e) => patchDeliverable(d.id, { dueDate: e.target.value || undefined })}
                  placeholder="Due YYYY-MM-DD"
                />
                <input
                  className="glass-input"
                  value={d.owner ?? ''}
                  onChange={(e) => patchDeliverable(d.id, { owner: e.target.value || undefined })}
                  placeholder="Owner"
                />
              </div>
            </div>
            <RemoveBtn
              onClick={() => set('deliverables', deliverables.filter((x) => x.id !== d.id))}
            />
          </div>
        ))}
      </EditListSection>

      {/* Billing schedule */}
      <EditListSection
        icon={Receipt}
        label="Billing schedule"
        onAdd={() =>
          set('billingDates', [
            ...billing,
            { id: uuid(), label: '', date: '', status: 'scheduled' },
          ])
        }
      >
        {billing.map((b) => (
          <div key={b.id} className="glass-row" style={{ alignItems: 'flex-start', gap: 8 }}>
            <div style={{ flex: 1, display: 'grid', gap: 6 }}>
              <input
                className="glass-input"
                value={b.label}
                onChange={(e) => patchBilling(b.id, { label: e.target.value })}
                placeholder="e.g. Phase 1 invoice"
              />
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 6 }}>
                <input
                  className="glass-input"
                  value={b.date}
                  onChange={(e) => patchBilling(b.id, { date: e.target.value })}
                  placeholder="YYYY-MM-DD"
                />
                <input
                  className="glass-input"
                  value={b.amount !== undefined ? String(b.amount) : ''}
                  onChange={(e) =>
                    patchBilling(b.id, { amount: e.target.value ? Number(e.target.value) : undefined })
                  }
                  placeholder="Amount USD"
                />
                <select
                  className="glass-input"
                  value={b.status ?? 'scheduled'}
                  onChange={(e) =>
                    patchBilling(b.id, { status: e.target.value as BillingDate['status'] })
                  }
                  style={{ fontFamily: 'Manrope, sans-serif' }}
                >
                  {(['scheduled', 'invoiced', 'paid', 'overdue'] as const).map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>
            </div>
            <RemoveBtn onClick={() => set('billingDates', billing.filter((x) => x.id !== b.id))} />
          </div>
        ))}
      </EditListSection>

      {/* Points of contact */}
      <EditListSection
        icon={Users}
        label="Points of contact"
        onAdd={() => set('pocs', [...pocs, { id: uuid(), name: '' }])}
      >
        {pocs.map((p) => (
          <div key={p.id} className="glass-row" style={{ alignItems: 'flex-start', gap: 8 }}>
            <div style={{ flex: 1, display: 'grid', gap: 6 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                <input
                  className="glass-input"
                  value={p.name}
                  onChange={(e) => patchPoc(p.id, { name: e.target.value })}
                  placeholder="Name"
                />
                <input
                  className="glass-input"
                  value={p.title ?? ''}
                  onChange={(e) => patchPoc(p.id, { title: e.target.value || undefined })}
                  placeholder="Title"
                />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                <input
                  className="glass-input"
                  value={p.email ?? ''}
                  onChange={(e) => patchPoc(p.id, { email: e.target.value || undefined })}
                  placeholder="Email"
                />
                <input
                  className="glass-input"
                  value={p.phone ?? ''}
                  onChange={(e) => patchPoc(p.id, { phone: e.target.value || undefined })}
                  placeholder="Phone"
                />
              </div>
              <label
                className="t-meta"
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}
              >
                <input
                  type="checkbox"
                  checked={!!p.primary}
                  onChange={(e) => patchPoc(p.id, { primary: e.target.checked })}
                />
                Primary contact
              </label>
            </div>
            <RemoveBtn onClick={() => set('pocs', pocs.filter((x) => x.id !== p.id))} />
          </div>
        ))}
      </EditListSection>
    </div>
  );
}

/** A titled editable-list block with an "+ Add" affordance + empty state. */
function EditListSection({
  icon: Icon,
  label,
  onAdd,
  children,
}: {
  icon: LucideIcon;
  label: string;
  onAdd: () => void;
  children: React.ReactNode;
}) {
  const isEmpty = Array.isArray(children) && children.length === 0;
  return (
    <section>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 6,
        }}
      >
        <div
          className="t-eyebrow"
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
        >
          <Icon size={11} /> {label}
        </div>
        <button className="glass-btn" onClick={onAdd}>
          <Plus size={12} /> Add
        </button>
      </div>
      {isEmpty ? (
        <span className="t-meta">None yet — add one above.</span>
      ) : (
        <div style={{ display: 'grid', gap: 8 }}>{children}</div>
      )}
    </section>
  );
}

/** Small icon-only remove button for editable rows. */
function RemoveBtn({ onClick }: { onClick: () => void }) {
  return (
    <button className="glass-btn" onClick={onClick} title="Remove" style={{ flexShrink: 0 }}>
      <Trash2 size={12} />
    </button>
  );
}

// --- Field primitives ------------------------------------------------------

function FieldGrid({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
      {children}
    </div>
  );
}

function Field({
  icon: Icon,
  label,
  value,
}: {
  icon: LucideIcon;
  label: string;
  value?: string;
}) {
  return (
    <div
      style={{
        padding: '10px 12px',
        borderRadius: 12,
        background: 'rgba(255,255,255,0.04)',
        border: '1px solid rgba(255,255,255,0.06)',
      }}
    >
      <div
        className="t-meta"
        style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginBottom: 2 }}
      >
        <Icon size={11} /> {label}
      </div>
      <div className="t-card-title" style={{ color: value ? C.text : C.textDim }}>
        {value ?? '—'}
      </div>
    </div>
  );
}

function EditField({
  label,
  value,
  onChange,
  placeholder,
}: {
  label: string;
  value?: string;
  onChange: (v: string | undefined) => void;
  placeholder?: string;
}) {
  return (
    <div>
      <div className="t-eyebrow" style={{ marginBottom: 4 }}>{label}</div>
      <input
        className="glass-input"
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value || undefined)}
        placeholder={placeholder}
      />
    </div>
  );
}

function EditSelect({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string;
  options: string[];
  onChange: (v: string) => void;
}) {
  return (
    <div>
      <div className="t-eyebrow" style={{ marginBottom: 4 }}>{label}</div>
      <select
        className="glass-input"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={{ width: '100%', fontFamily: 'Manrope, sans-serif' }}
      >
        {options.map((o) => (
          <option key={o} value={o}>
            {o || '— none —'}
          </option>
        ))}
      </select>
    </div>
  );
}

// --- Win-loss intelligence -------------------------------------------------

/**
 * The win-loss rollup that sits atop the prospects tab. Deterministic and
 * computed straight from closed prospects via `crm/winloss.ts` — win rate,
 * why-we-win / why-we-lose, and conversion by source. Shows an honest
 * sparse state until deals actually close, so it never fakes a stat.
 */
function WinLossPanel({ people }: { people: Person[] }) {
  const wl = useMemo(() => computeWinLoss(people), [people]);
  const total = wl.won + wl.lost;
  if (total === 0) {
    return (
      <GlassPanel variant="subtle" animate={false} style={{ padding: '12px 16px' }}>
        <div className="t-eyebrow" style={{ marginBottom: 4 }}>
          Win-loss intelligence
        </div>
        <div className="t-meta" style={{ color: C.textDim }}>
          No closed deals yet. Move a prospect to closed-won or closed-lost and
          capture the reason — the win rate and reason breakdowns build from
          there.
        </div>
      </GlassPanel>
    );
  }

  const usd = (n: number): string =>
    n >= 1000 ? `$${(n / 1000).toFixed(n >= 10000 ? 0 : 1)}k` : `$${n}`;
  const rateColor =
    wl.winRate === null
      ? C.textDim
      : wl.winRate >= 60
        ? '#10b981'
        : wl.winRate >= 35
          ? '#f59e0b'
          : '#f43f5e';

  return (
    <GlassPanel variant="subtle" animate={false} style={{ padding: '14px 16px' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          gap: 12,
          marginBottom: 12,
          flexWrap: 'wrap',
        }}
      >
        <div className="t-eyebrow">Win-loss intelligence</div>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 14, flexWrap: 'wrap' }}>
          <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: 5 }}>
            <span className="t-card-title" style={{ color: rateColor, fontSize: 20 }}>
              {wl.winRate}%
            </span>
            <span className="t-meta" style={{ color: C.textDim }}>win rate</span>
          </span>
          <span className="t-meta" style={{ color: '#10b981' }}>
            {wl.won} won · {usd(wl.wonValue)}
          </span>
          <span className="t-meta" style={{ color: '#f43f5e' }}>
            {wl.lost} lost · {usd(wl.lostValue)}
          </span>
        </div>
      </div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: 14,
        }}
      >
        <ReasonColumn title="Why we win" accent="#10b981" rows={wl.wonReasons} />
        <ReasonColumn title="Why we lose" accent="#f43f5e" rows={wl.lostReasons} />
        <SegmentColumn title="By source" rows={wl.bySource} />
      </div>
    </GlassPanel>
  );
}

function ReasonColumn({
  title,
  accent,
  rows,
}: {
  title: string;
  accent: string;
  rows: ReasonTally[];
}) {
  return (
    <div>
      <div className="t-meta" style={{ color: accent, marginBottom: 6 }}>{title}</div>
      {rows.length === 0 ? (
        <div className="t-meta" style={{ color: C.textDim }}>—</div>
      ) : (
        <div style={{ display: 'grid', gap: 4 }}>
          {rows.map((r) => (
            <div
              key={r.reason}
              style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}
            >
              <span className="t-meta" style={{ color: C.textMuted }}>{r.reason}</span>
              <span className="t-meta" style={{ color: C.text }}>{r.count}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function SegmentColumn({ title, rows }: { title: string; rows: SegmentStat[] }) {
  return (
    <div>
      <div className="t-meta" style={{ color: C.textMuted, marginBottom: 6 }}>{title}</div>
      {rows.length === 0 ? (
        <div className="t-meta" style={{ color: C.textDim }}>—</div>
      ) : (
        <div style={{ display: 'grid', gap: 4 }}>
          {rows.map((s) => (
            <div
              key={s.label}
              style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}
            >
              <span className="t-meta" style={{ color: C.textMuted }}>{s.label}</span>
              <span className="t-meta" style={{ color: C.text }}>
                {s.winRate === null ? '—' : `${s.winRate}%`}
                <span style={{ color: C.textDim }}> ({s.won + s.lost})</span>
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// --- Personas: structured ICP creator --------------------------------------

/**
 * Personas — ICP archetypes you can spin up in one click from a starter
 * template or synthesize from the real CRM, then refine inline. Backed by
 * the `people` collection (category `persona`).
 */
function PersonasPanel({
  personas,
  allPeople,
  canMutate,
}: {
  personas: Person[];
  allPeople: Person[];
  canMutate: boolean;
}) {
  const [spinUp, setSpinUp] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);

  const editing = editId ? personas.find((p) => p.id === editId) ?? null : null;
  const sorted = [...personas].sort((a, b) => a.name.localeCompare(b.name));

  const create = async (draft: PersonaDraft) => {
    const created = await createPerson({
      category: 'persona',
      name: draft.name.trim() || 'New persona',
      archetype: draft.archetype.trim() || undefined,
      summary: draft.summary.trim() || undefined,
      tags: draft.tags,
      profile: cleanProfile(draft.profile),
    });
    setSpinUp(false);
    setEditId(created.id);
  };

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      {/* Purpose header — always visible so "what is this for?" never needs
          asking: what a persona is, where it feeds, and the create CTA. */}
      <GlassPanel style={{ padding: '18px 20px' }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 14,
            flexWrap: 'wrap',
          }}
        >
          <span
            aria-hidden="true"
            style={{
              width: 40,
              height: 40,
              borderRadius: 12,
              display: 'inline-grid',
              placeItems: 'center',
              background:
                'linear-gradient(135deg, rgba(253,173,1,0.22), rgba(226,57,57,0.12))',
              border: '1px solid rgba(255,255,255,0.14)',
              color: '#ffd06b',
              flex: '0 0 auto',
            }}
          >
            <ScanFace size={20} strokeWidth={1.75} />
          </span>
          <div style={{ flex: '1 1 320px', minWidth: 0, display: 'grid', gap: 6 }}>
            <h3 className="t-section-title" style={{ margin: 0 }}>
              Personas — the buyer profiles that tune everything downstream
            </h3>
            <p className="t-body" style={{ margin: 0, color: C.textMuted }}>
              {PERSONA_PURPOSE}
            </p>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 2 }}>
              {PERSONA_FEEDS.map((feed) => {
                const FeedIcon =
                  feed.id === 'content' ? Wand2 : feed.id === 'outreach' ? Send : Target;
                return (
                  <span
                    key={feed.id}
                    className="glass-pill"
                    title={feed.detail}
                    style={{
                      fontSize: 10,
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 5,
                    }}
                  >
                    <FeedIcon size={11} aria-hidden="true" /> Feeds {feed.label}
                  </span>
                );
              })}
            </div>
          </div>
          {canMutate && (
            <button
              className="glass-btn glass-btn--primary"
              onClick={() => setSpinUp(true)}
              style={{ flex: '0 0 auto' }}
            >
              <Sparkles size={13} /> Spin up persona
            </button>
          )}
        </div>
      </GlassPanel>

      {sorted.length === 0 ? (
        <GlassPanel style={{ padding: '40px 32px' }}>
          <div
            style={{
              display: 'grid',
              placeItems: 'center',
              gap: 12,
              textAlign: 'center',
              maxWidth: 520,
              margin: '0 auto',
            }}
          >
            <span
              style={{
                width: 56,
                height: 56,
                borderRadius: 16,
                display: 'inline-grid',
                placeItems: 'center',
                background:
                  'linear-gradient(135deg, rgba(253,173,1,0.22), rgba(226,57,57,0.12))',
                border: '1px solid rgba(255,255,255,0.14)',
                color: '#ffd06b',
              }}
            >
              <ScanFace size={26} strokeWidth={1.75} />
            </span>
            <h3 className="t-section-title" style={{ margin: 0 }}>
              No personas yet
            </h3>
            <p className="t-body" style={{ color: C.textMuted, margin: 0 }}>
              Start from a ready-made template — Agency Owner, RevOps Lead,
              Marketing Director, Founder/CEO — or synthesize one from your real
              prospects and customers. Every field comes pre-filled; tweak and
              create in under a minute.
            </p>
            {canMutate && (
              <button
                className="glass-btn glass-btn--primary"
                style={{ marginTop: 4 }}
                onClick={() => setSpinUp(true)}
              >
                <Sparkles size={13} /> Spin up your first persona
              </button>
            )}
          </div>
        </GlassPanel>
      ) : (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
            gap: 14,
          }}
        >
          {sorted.map((p) => (
            <PersonaCard key={p.id} persona={p} onOpen={() => setEditId(p.id)} />
          ))}
        </div>
      )}

      {spinUp && (
        <PersonaSpinUpModal
          allPeople={allPeople}
          onClose={() => setSpinUp(false)}
          onCreate={create}
        />
      )}
      {editing && (
        <PersonaEditModal
          persona={editing}
          canMutate={canMutate}
          onClose={() => setEditId(null)}
        />
      )}
    </div>
  );
}

function PersonaCard({
  persona,
  onOpen,
}: {
  persona: Person;
  onOpen: () => void;
}) {
  // Profile completeness meter — richer profiles produce sharper drafts and
  // outreach, so each card shows how filled-in its twelve ICP dimensions are.
  const completeness = personaCompleteness(persona.profile);
  return (
    <GlassPanel
      variant="default"
      animate={false}
      style={{ padding: 0, overflow: 'hidden' }}
    >
      <button
        type="button"
        onClick={onOpen}
        className="glass-row"
        style={{
          width: '100%',
          textAlign: 'left',
          border: 'none',
          background: 'transparent',
          padding: '16px 16px',
          display: 'grid',
          gap: 10,
          cursor: 'pointer',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span
            aria-hidden="true"
            style={{
              width: 34,
              height: 34,
              borderRadius: 10,
              display: 'inline-grid',
              placeItems: 'center',
              background:
                'linear-gradient(135deg, rgba(253,173,1,0.22), rgba(226,57,57,0.12))',
              border: '1px solid rgba(255,255,255,0.14)',
              color: '#ffd06b',
              flex: '0 0 auto',
            }}
          >
            <ScanFace size={17} strokeWidth={2} />
          </span>
          <div style={{ minWidth: 0 }}>
            <span
              className="t-card-title"
              style={{
                display: 'block',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {persona.name}
            </span>
            {persona.archetype && (
              <span className="t-meta" style={{ display: 'block' }}>
                {persona.archetype}
              </span>
            )}
          </div>
          <span
            className="glass-pill"
            style={{ fontSize: 10, marginLeft: 'auto', flex: '0 0 auto' }}
            title={
              completeness.missing.length > 0
                ? `Profile ${completeness.pct}% complete — add ${completeness.missing
                    .slice(0, 3)
                    .join(', ')}${completeness.missing.length > 3 ? '…' : ''} for sharper drafts and outreach`
                : 'Profile fully filled in — all twelve ICP dimensions defined'
            }
          >
            {completeness.filled}/{completeness.total} profile
          </span>
        </div>
        {persona.summary && (
          <p
            className="t-body"
            style={{
              margin: 0,
              color: C.textMuted,
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {persona.summary}
          </p>
        )}

        <PersonaProfilePreview profile={persona.profile} />

        {persona.tags.length > 0 && (
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {persona.tags.slice(0, 4).map((t) => (
              <span key={t} className="glass-pill" style={{ fontSize: 10 }}>
                {t}
              </span>
            ))}
          </div>
        )}
      </button>
    </GlassPanel>
  );
}

/**
 * Compact structured-profile preview rendered inside a PersonaCard:
 * a meta-chip row (seniority / company size / industry) plus the top
 * goals and pain points. Renders nothing when the profile is empty.
 */
function PersonaProfilePreview({ profile }: { profile?: PersonaProfile }) {
  if (!profile) return null;
  const chips = [profile.seniority, profile.companySize, profile.industry].filter(
    (v): v is string => !!v && v.trim().length > 0,
  );
  const goals = (profile.goals ?? []).filter(Boolean);
  const pains = (profile.painPoints ?? []).filter(Boolean);
  if (chips.length === 0 && goals.length === 0 && pains.length === 0) return null;

  return (
    <div style={{ display: 'grid', gap: 8 }}>
      {chips.length > 0 && (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {chips.map((c) => (
            <span key={c} className="glass-pill glass-pill--blue" style={{ fontSize: 10 }}>
              {c}
            </span>
          ))}
        </div>
      )}
      {goals.length > 0 && (
        <CardMiniList label="Goals" items={goals.slice(0, 3)} color={C.green} />
      )}
      {pains.length > 0 && (
        <CardMiniList label="Pain points" items={pains.slice(0, 3)} color={C.orangeWarm} />
      )}
    </div>
  );
}

/** A tiny labelled bullet list for the persona card preview. */
function CardMiniList({
  label,
  items,
  color,
}: {
  label: string;
  items: string[];
  color: string;
}) {
  return (
    <div style={{ display: 'grid', gap: 3 }}>
      <span className="t-eyebrow" style={{ color: C.textDim }}>
        {label}
      </span>
      {items.map((it, i) => (
        <div key={i} style={{ display: 'flex', gap: 6, alignItems: 'baseline' }}>
          <span
            aria-hidden="true"
            style={{
              width: 4,
              height: 4,
              borderRadius: 99,
              background: color,
              flex: '0 0 auto',
              marginTop: 5,
            }}
          />
          <span className="t-meta" style={{ color: C.textMuted }}>
            {it}
          </span>
        </div>
      ))}
    </div>
  );
}

/**
 * Normalize a structured persona profile for persistence: trim the
 * scalar fields, drop blank list lines, and collapse the whole thing to
 * `undefined` when nothing meaningful is left so we never store a hollow
 * object on the person record.
 */
function cleanProfile(profile?: PersonaProfile): PersonaProfile | undefined {
  if (!profile) return undefined;
  const scalar = (v?: string) => {
    const t = (v ?? '').trim();
    return t.length > 0 ? t : undefined;
  };
  const list = (xs?: string[]) =>
    (xs ?? []).map((x) => x.trim()).filter((x) => x.length > 0);

  const out: PersonaProfile = {
    role: scalar(profile.role),
    seniority: scalar(profile.seniority),
    companySize: scalar(profile.companySize),
    industry: scalar(profile.industry),
    goals: list(profile.goals),
    painPoints: list(profile.painPoints),
    motivations: list(profile.motivations),
    objections: list(profile.objections),
    decisionCriteria: list(profile.decisionCriteria),
    channels: list(profile.channels),
    kpis: list(profile.kpis),
    quote: scalar(profile.quote),
  };

  const hasScalar = !!(out.role || out.seniority || out.companySize || out.industry || out.quote);
  const hasList = [
    out.goals,
    out.painPoints,
    out.motivations,
    out.objections,
    out.decisionCriteria,
    out.channels,
    out.kpis,
  ].some((l) => (l?.length ?? 0) > 0);

  return hasScalar || hasList ? out : undefined;
}

/** Multi-line list editor: one item per line, split back into a string[]. */
function ListField({
  label,
  items,
  onChange,
  placeholder,
}: {
  label: string;
  items: string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
}) {
  return (
    <ModalField label={label}>
      <textarea
        className="glass-input"
        value={(items ?? []).join('\n')}
        onChange={(e) => onChange(e.target.value.split('\n'))}
        rows={3}
        placeholder={placeholder ?? 'One per line…'}
        style={{ resize: 'vertical', lineHeight: 1.5 }}
      />
    </ModalField>
  );
}

/**
 * Shared structured-ICP editor used by both the spin-up and edit modals.
 * Renders the scalar fields (role / seniority / company size / industry /
 * quote) plus a line-based editor for each list dimension of the persona
 * profile.
 */
function PersonaProfileFields({
  profile,
  onChange,
}: {
  profile: PersonaProfile;
  onChange: (next: PersonaProfile) => void;
}) {
  const set = <K extends keyof PersonaProfile>(key: K, value: PersonaProfile[K]) =>
    onChange({ ...profile, [key]: value });

  return (
    <div style={{ display: 'grid', gap: 14 }}>
      <span className="t-eyebrow" style={{ color: C.textDim }}>
        Ideal-customer profile
      </span>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <ModalField label="Role / title">
          <input
            className="glass-input"
            value={profile.role ?? ''}
            onChange={(e) => set('role', e.target.value)}
            placeholder="e.g. VP of RevOps"
          />
        </ModalField>
        <ModalField label="Seniority">
          <select
            className="glass-input"
            value={profile.seniority ?? ''}
            onChange={(e) => set('seniority', e.target.value)}
          >
            {SENIORITY_OPTIONS.map((o) => (
              <option key={o} value={o}>
                {o || '—'}
              </option>
            ))}
          </select>
        </ModalField>
        <ModalField label="Company size">
          <select
            className="glass-input"
            value={profile.companySize ?? ''}
            onChange={(e) => set('companySize', e.target.value)}
          >
            {COMPANY_SIZE_OPTIONS.map((o) => (
              <option key={o} value={o}>
                {o || '—'}
              </option>
            ))}
          </select>
        </ModalField>
        <ModalField label="Industry">
          <input
            className="glass-input"
            value={profile.industry ?? ''}
            onChange={(e) => set('industry', e.target.value)}
            placeholder="e.g. B2B SaaS / Services"
          />
        </ModalField>
      </div>

      <ListField
        label="Goals"
        items={profile.goals ?? []}
        onChange={(v) => set('goals', v)}
        placeholder="What this persona is trying to achieve…"
      />
      <ListField
        label="Pain points"
        items={profile.painPoints ?? []}
        onChange={(v) => set('painPoints', v)}
        placeholder="What hurts today…"
      />
      <ListField
        label="Motivations"
        items={profile.motivations ?? []}
        onChange={(v) => set('motivations', v)}
      />
      <ListField
        label="Objections"
        items={profile.objections ?? []}
        onChange={(v) => set('objections', v)}
      />
      <ListField
        label="Decision criteria"
        items={profile.decisionCriteria ?? []}
        onChange={(v) => set('decisionCriteria', v)}
      />
      <ListField
        label="Channels"
        items={profile.channels ?? []}
        onChange={(v) => set('channels', v)}
      />
      <ListField label="KPIs" items={profile.kpis ?? []} onChange={(v) => set('kpis', v)} />

      <ModalField label="Representative quote">
        <textarea
          className="glass-input"
          value={profile.quote ?? ''}
          onChange={(e) => set('quote', e.target.value)}
          rows={2}
          placeholder="Something this persona would actually say…"
          style={{ resize: 'vertical', lineHeight: 1.5 }}
        />
      </ModalField>
    </div>
  );
}

/** Spin-up modal: pick a starter template (or synthesize from CRM), tweak, create. */
function PersonaSpinUpModal({
  allPeople,
  onClose,
  onCreate,
}: {
  allPeople: Person[];
  onClose: () => void;
  onCreate: (draft: PersonaDraft) => Promise<void>;
}) {
  const [templateId, setTemplateId] = useState<string>(PERSONA_TEMPLATES[0].id);
  const [name, setName] = useState(PERSONA_TEMPLATES[0].label);
  const [archetype, setArchetype] = useState(PERSONA_TEMPLATES[0].archetype);
  const [summary, setSummary] = useState(PERSONA_TEMPLATES[0].summary);
  const [tags, setTags] = useState<string[]>(PERSONA_TEMPLATES[0].tags);
  const [profile, setProfile] = useState<PersonaProfile>(PERSONA_TEMPLATES[0].profile);
  const [busy, setBusy] = useState(false);

  const pick = (id: string) => {
    const tpl = personaTemplate(id);
    setTemplateId(id);
    setName(tpl.label === 'Custom persona' ? '' : tpl.label);
    setArchetype(tpl.archetype);
    setSummary(tpl.summary);
    setTags(tpl.tags);
    setProfile(tpl.profile);
  };

  const generate = () => {
    const draft = synthesizePersona(allPeople);
    setTemplateId('custom');
    setName(draft.name);
    setArchetype(draft.archetype);
    setSummary(draft.summary);
    setTags(draft.tags);
    setProfile(draft.profile);
  };

  // Validate live: create is gated on a name (unique among existing
  // personas) plus at least an archetype or summary — the minimum signal
  // the downstream consumers (content, outreach, scoring) need.
  const existingNames = allPeople
    .filter((p) => p.category === 'persona')
    .map((p) => p.name);
  const issues = validatePersonaDraft({ name, archetype, summary }, existingNames);

  const submit = async () => {
    if (issues.length > 0) return;
    setBusy(true);
    try {
      await onCreate({ name, archetype, summary, tags, profile });
    } finally {
      setBusy(false);
    }
  };

  const templateGroup = (
    label: string,
    templates: PersonaTemplate[],
  ): ReactNode => (
    <div style={{ display: 'grid', gap: 8 }}>
      <span className="t-eyebrow">{label}</span>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
          gap: 8,
        }}
      >
        {templates.map((tpl) => {
          const active = tpl.id === templateId;
          return (
            <button
              key={tpl.id}
              type="button"
              onClick={() => pick(tpl.id)}
              className="glass-btn"
              style={{
                padding: '10px 12px',
                textAlign: 'left',
                display: 'grid',
                gap: 4,
                border: `1px solid ${active ? 'rgba(255,0,255,0.45)' : 'rgba(255,255,255,0.10)'}`,
                background: active
                  ? 'linear-gradient(135deg, rgba(255,0,255,0.18), rgba(187,65,152,0.10))'
                  : 'transparent',
              }}
            >
              <span className="t-card-title" style={{ fontSize: 12 }}>
                {tpl.label}
              </span>
              <span className="t-meta">{tpl.blurb}</span>
            </button>
          );
        })}
      </div>
    </div>
  );

  return (
    <ModalShell onClose={onClose} title="Spin up a persona" icon={Sparkles}>
      <p className="t-meta" style={{ margin: 0 }}>
        Every template is fully pre-filled — pick one, tweak what's off, and
        create. The persona then feeds Content Engine drafts, outreach tone,
        and win/loss scoring.
      </p>

      {/* Template picker — role starters first, then buying-committee seats. */}
      <div style={{ display: 'grid', gap: 12 }}>
        {templateGroup(
          'Start from who they are',
          PERSONA_TEMPLATES.filter((t) => t.group === 'role'),
        )}
        {templateGroup(
          '…or their seat in the deal',
          PERSONA_TEMPLATES.filter((t) => t.group !== 'role'),
        )}
        <button
          type="button"
          className="glass-btn"
          onClick={generate}
          style={{ justifySelf: 'start', marginTop: 2 }}
        >
          <Wand2 size={13} /> Generate from CRM
        </button>
      </div>

      <ModalField label="Name">
        <input
          className="glass-input"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. VP of RevOps (ICP)"
        />
      </ModalField>
      <ModalField label="Archetype">
        <input
          className="glass-input"
          value={archetype}
          onChange={(e) => setArchetype(e.target.value)}
          placeholder="e.g. Economic Buyer"
        />
      </ModalField>
      <ModalField label="Summary">
        <textarea
          className="glass-input"
          value={summary}
          onChange={(e) => setSummary(e.target.value)}
          rows={5}
          placeholder="Who they are, what they care about, their pains and objections…"
          style={{ resize: 'vertical', lineHeight: 1.5 }}
        />
      </ModalField>

      <PersonaProfileFields profile={profile} onChange={setProfile} />

      {/* Live validation — explain exactly what blocks Create, never a
          silently disabled button. */}
      {issues.length > 0 && (
        <div style={{ display: 'grid', gap: 4 }}>
          {issues.map((issue) => (
            <span
              key={issue.field}
              className="t-meta"
              style={{ color: '#ffb08a', display: 'flex', alignItems: 'center', gap: 6 }}
            >
              <AlertTriangle size={12} aria-hidden="true" /> {issue.message}
            </span>
          ))}
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 4 }}>
        <button className="glass-btn" onClick={onClose} disabled={busy}>
          Cancel
        </button>
        <button
          className="glass-btn glass-btn--primary"
          onClick={submit}
          disabled={busy || issues.length > 0}
          title={issues.length > 0 ? issues[0].message : undefined}
        >
          <Sparkles size={13} /> Create persona
        </button>
      </div>
    </ModalShell>
  );
}

/** Edit / delete an existing persona (read-only view for viewers). */
function PersonaEditModal({
  persona,
  canMutate,
  onClose,
}: {
  persona: Person;
  canMutate: boolean;
  onClose: () => void;
}) {
  const [name, setName] = useState(persona.name);
  const [archetype, setArchetype] = useState(persona.archetype ?? '');
  const [summary, setSummary] = useState(persona.summary ?? '');
  const [profile, setProfile] = useState<PersonaProfile>(persona.profile ?? {});
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      await updatePerson(persona.id, {
        name: name.trim() || persona.name,
        archetype: archetype.trim() || undefined,
        summary: summary.trim() || undefined,
        profile: cleanProfile(profile),
      });
      onClose();
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await deletePerson(persona.id);
      onClose();
    } finally {
      setBusy(false);
    }
  };

  return (
    <ModalShell
      onClose={onClose}
      title={canMutate ? 'Edit persona' : 'Persona'}
      icon={ScanFace}
    >
      <ModalField label="Name">
        <input
          className="glass-input"
          value={name}
          onChange={(e) => setName(e.target.value)}
          readOnly={!canMutate}
        />
      </ModalField>
      <ModalField label="Archetype">
        <input
          className="glass-input"
          value={archetype}
          onChange={(e) => setArchetype(e.target.value)}
          readOnly={!canMutate}
        />
      </ModalField>
      <ModalField label="Summary">
        <textarea
          className="glass-input"
          value={summary}
          onChange={(e) => setSummary(e.target.value)}
          rows={6}
          readOnly={!canMutate}
          style={{ resize: 'vertical', lineHeight: 1.5 }}
        />
      </ModalField>

      {canMutate ? (
        <PersonaProfileFields profile={profile} onChange={setProfile} />
      ) : (
        <PersonaProfilePreview profile={profile} />
      )}

      {persona.tags.length > 0 && (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {persona.tags.map((t) => (
            <span key={t} className="glass-pill" style={{ fontSize: 10 }}>
              {t}
            </span>
          ))}
        </div>
      )}

      {/* Where this persona is consumed — the "why keep it sharp" reminder. */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {PERSONA_FEEDS.map((feed) => (
          <span
            key={feed.id}
            className="glass-pill"
            title={feed.detail}
            style={{ fontSize: 10 }}
          >
            Feeds {feed.label}
          </span>
        ))}
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, marginTop: 4 }}>
        {canMutate ? (
          <>
            <button
              className="glass-btn"
              onClick={remove}
              disabled={busy}
              style={{ color: C.magenta }}
            >
              <Trash2 size={13} /> Delete
            </button>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="glass-btn" onClick={onClose} disabled={busy}>
                Cancel
              </button>
              <button className="glass-btn glass-btn--primary" onClick={save} disabled={busy}>
                <Check size={13} /> Save
              </button>
            </div>
          </>
        ) : (
          <button className="glass-btn" onClick={onClose} style={{ marginLeft: 'auto' }}>
            Close
          </button>
        )}
      </div>
    </ModalShell>
  );
}

/** Shared centered modal chrome for the persona flows. */
function ModalShell({
  title,
  icon: Icon,
  onClose,
  children,
}: {
  title: string;
  icon: LucideIcon;
  onClose: () => void;
  children: React.ReactNode;
}) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 90,
        background: 'rgba(6,6,12,0.55)',
        backdropFilter: 'blur(3px)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'flex-start',
        overflowY: 'auto',
        padding: '24px 16px',
      }}
    >
      <div onClick={(e) => e.stopPropagation()} style={{ width: 'min(560px, 100%)', marginTop: 24 }}>
        <GlassPanel variant="sharp" animate={false} style={{ padding: '24px 26px' }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: 16,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span
                aria-hidden="true"
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: 9,
                  display: 'inline-grid',
                  placeItems: 'center',
                  background:
                    'linear-gradient(135deg, rgba(253,173,1,0.22), rgba(226,57,57,0.12))',
                  border: '1px solid rgba(255,255,255,0.14)',
                  color: '#ffd06b',
                }}
              >
                <Icon size={16} strokeWidth={2} />
              </span>
              <h3 className="t-section-title" style={{ margin: 0 }}>
                {title}
              </h3>
            </div>
            <button
              className="glass-btn"
              aria-label="Close"
              onClick={onClose}
              style={{ padding: 6 }}
            >
              <X size={15} />
            </button>
          </div>
          <div style={{ display: 'grid', gap: 14 }}>{children}</div>
        </GlassPanel>
      </div>
    </div>
  );
}

function ModalField({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <label style={{ display: 'grid', gap: 6 }}>
      <span className="t-eyebrow">{label}</span>
      {children}
    </label>
  );
}

// --- Helpers ---------------------------------------------------------------

function fmtUsd(n: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(n);
}

function fmtDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return iso;
  }
}

/** Compact relative time for the activity rail: "just now", "3h ago", else date. */
function fmtWhen(iso: string): string {
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return iso;
  const mins = Math.round((Date.now() - t) / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.round(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return fmtDate(iso);
}
